import { useState, useEffect, useCallback } from 'react';
import VehiclesCostingPanel from './costing/VehiclesCostingPanel';
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '../../../components/ui/dialog';
import { Badge } from '../../../components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../../components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../../components/ui/tabs';
import ExpensesByMonthPanel from './ExpensesByMonthPanel';
import { Popover, PopoverContent, PopoverTrigger } from '../../../components/ui/popover';
import { Switch } from '../../../components/ui/switch';
import PartsCatalogPanel from '../../auto-spares/PartsCatalog';
import CashUpPanel from './CashUpPanel';
import InvoicesPanel from './InvoicesPanel';
import CreditsPanel from './CreditsPanel';
import StockTakePanel from './StockTakePanel';
import CostPriceImportPanel from './CostPriceImportPanel';
import ExpansionBudgetPanel from './ExpansionBudgetPanel';
import SparesPnLPanel from './SparesPnLPanel';
import { ProcurementContent } from '../../procurement/ProcurementPage';
import { XlsxImportExport } from '../../../components/XlsxImportExport';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend, Line, ComposedChart, ReferenceLine } from 'recharts';
import {
  Users, TrendingUp, DollarSign, Plus, Calendar, Package, Pencil, Trash2,
  LogOut, Receipt, TrendingDown, Warehouse, Truck, Banknote, PiggyBank, BarChart3, Printer, MessageCircle, Home, Search, History, X, FileDown, LineChart as LineChartIcon, AlertTriangle, CheckCircle2
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const EXPENSE_CATEGORIES = ['Fuel', 'Stock Purchase', 'Rent', 'Utilities', 'Transport', 'Food', 'Repairs', 'Tools', 'Marketing', 'General'];

export default function AdminDashboard() {
  const [dashboardData, setDashboardData] = useState(null);
  const [salesmen, setSalesmen] = useState([]);
  const [todaySales, setTodaySales] = useState([]);
  const [expenses, setExpenses] = useState([]);
  const [expenseTotals, setExpenseTotals] = useState({ today_total: 0, month_total: 0 });
  // Year-to-date expense analytics (by category + by month) — surfaced under
  // the Expenses tab so admins can see where the money actually goes.
  const [expensesYtd, setExpensesYtd] = useState(null);
  const [showYtd, setShowYtd] = useState(true);
  const [products, setProducts] = useState([]);
  const [futureValue, setFutureValue] = useState(null); // { total_future_value, products_with_history, ... }
  const [categories, setCategories] = useState([]);
  const [stockPurchases, setStockPurchases] = useState([]);
  const [capitalInjections, setCapitalInjections] = useState([]);
  const [debtRepayments, setDebtRepayments] = useState([]);
  const [salaries, setSalaries] = useState([]);
  const [trueProfit, setTrueProfit] = useState(null);
  const [trueProfitScope, setTrueProfitScope] = useState('month'); // 'month' | 'year'
  const [investmentData, setInvestmentData] = useState(null); // { months, totals, forecast }
  const [loading, setLoading] = useState(true);

  // Dialog states
  const [showAddSalesman, setShowAddSalesman] = useState(false);
  const [showEditSalesman, setShowEditSalesman] = useState(false);
  const [showAddSale, setShowAddSale] = useState(false);
  const [showEditSale, setShowEditSale] = useState(false);
  const [showAddExpense, setShowAddExpense] = useState(false);
  const [showEditExpense, setShowEditExpense] = useState(false);
  const [showAddProduct, setShowAddProduct] = useState(false);
  const [showWipeInventory, setShowWipeInventory] = useState(false);
  const [wipeConfirmText, setWipeConfirmText] = useState('');
  const [wiping, setWiping] = useState(false);
  const [showEditProduct, setShowEditProduct] = useState(false);
  const [showBulkPurchase, setShowBulkPurchase] = useState(false);
  const [showAddCapital, setShowAddCapital] = useState(false);
  const [showAddDebt, setShowAddDebt] = useState(false);
  const [showAddSalary, setShowAddSalary] = useState(false);

  // Edit states
  const [selectedSalesman, setSelectedSalesman] = useState(null);
  const [editingSale, setEditingSale] = useState(null);
  const [editingExpense, setEditingExpense] = useState(null);
  const [editingProduct, setEditingProduct] = useState(null);

  // Form states
  const [newSalesman, setNewSalesman] = useState({ full_name: '', email: '', password: '', commission_rate: 10, basic_salary: 2000 });
  // NEW: multi-line ring-up (up to 10 lines per sale ticket)
  const MAX_SALE_LINES = 10;
  const blankSaleLine = () => ({ _key: crypto.randomUUID(), part_description: '', quantity: 1, unit_price: '', product_id: '', _search: '', _open: false });
  const [saleSalesmanId, setSaleSalesmanId] = useState('');
  const [salePaymentMethod, setSalePaymentMethod] = useState('cash');
  const [saleCustomerName, setSaleCustomerName] = useState('');
  const [saleLines, setSaleLines] = useState([blankSaleLine()]);
  // Split-commission state on the Add Sale dialog
  const [splitOn, setSplitOn] = useState(false);
  const [splitSalesmen, setSplitSalesmen] = useState(['']); // up to 2 co-salesmen (primary is implicit)
  // Post-hoc split-commission dialog
  const [showSplitDialog, setShowSplitDialog] = useState(false);
  const [splitSale, setSplitSale] = useState(null); // the sale being split
  const [splitDialogRows, setSplitDialogRows] = useState([]); // [{salesman_id, percent}]
  // Searchable parts-catalog combobox for the "Add Sale" dialog
  const [catalogParts, setCatalogParts] = useState([]);
  const [newExpense, setNewExpense] = useState({ description: '', amount: '', category: 'General', date: new Date().toISOString().slice(0, 10) });
  const [newProduct, setNewProduct] = useState({ name: '', category: '', subcategory: '', cost_price: '', selling_price: '', quantity: 1, vehicle_source: '' });
  const [bulkPurchase, setBulkPurchase] = useState({ description: '', total_cost: '', items: [{ name: '', category: '', subcategory: '', quantity: 1, cost_allocation: '' }] });
  const [newCapital, setNewCapital] = useState({ description: '', amount: '', date: new Date().toISOString().slice(0, 10), source: '' });
  const [newDebt, setNewDebt] = useState({ description: '', amount: '', date: new Date().toISOString().slice(0, 10), paid_to: '' });
  const [newSalary, setNewSalary] = useState({ employee_name: '', amount: '', date: new Date().toISOString().slice(0, 10), period: '' });

  // ---- Sales History search ----
  const [salesSearchQuery, setSalesSearchQuery] = useState('');
  const [salesSearchStart, setSalesSearchStart] = useState('');
  const [salesSearchEnd, setSalesSearchEnd] = useState('');
  const [salesSearchResults, setSalesSearchResults] = useState([]);
  const [salesSearchCount, setSalesSearchCount] = useState(0);
  const [salesSearchTotalCount, setSalesSearchTotalCount] = useState(0);
  const [salesSearchTruncated, setSalesSearchTruncated] = useState(false);
  const [salesSearchTotal, setSalesSearchTotal] = useState(0);
  const [salesSearchLoading, setSalesSearchLoading] = useState(false);
  const [salesSearchRan, setSalesSearchRan] = useState(false);
  const [stockAlerts, setStockAlerts] = useState(null);
  const [showStockAlerts, setShowStockAlerts] = useState(false);

  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };
  const navigate = useNavigate();

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    navigate('/login');
  };

  const formatCurrency = (amount) => new Intl.NumberFormat('en-ZA', { style: 'currency', currency: 'ZAR' }).format(amount || 0);

  const fetchDashboard = useCallback(async () => {
    try {
      const [dashRes, salesmenRes, salesRes, expRes, ytdRes] = await Promise.all([
        axios.get(`${API_URL}/api/auto-spares/admin/dashboard`, { headers }),
        axios.get(`${API_URL}/api/auto-spares/admin/salesmen`, { headers }),
        axios.get(`${API_URL}/api/auto-spares/admin/all-sales-today`, { headers }),
        axios.get(`${API_URL}/api/auto-spares/admin/expenses`, { headers }),
        axios.get(`${API_URL}/api/auto-spares/admin/expenses/ytd`, { headers }).catch(() => ({ data: null })),
      ]);
      setDashboardData(dashRes.data);
      setSalesmen(salesmenRes.data);
      setTodaySales(salesRes.data);
      setExpenses(expRes.data.expenses || []);
      setExpenseTotals({ today_total: expRes.data.today_total || 0, month_total: expRes.data.month_total || 0 });
      setExpensesYtd(ytdRes.data || null);
    } catch (error) {
      toast.error('Failed to load dashboard');
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchInventory = useCallback(async () => {
    try {
      const [prodRes, catRes, purchRes, partsRes, fvRes, alertRes] = await Promise.all([
        axios.get(`${API_URL}/api/auto-spares/products`, { headers }),
        axios.get(`${API_URL}/api/auto-spares/categories`, { headers }),
        axios.get(`${API_URL}/api/auto-spares/stock-purchases`, { headers }),
        axios.get(`${API_URL}/api/parts-catalog?limit=2000`, { headers }),
        axios.get(`${API_URL}/api/auto-spares/products/future-value`, { headers }),
        axios.get(`${API_URL}/api/auto-spares/stock-alerts`, { headers }).catch(() => ({ data: null })),
      ]);
      setProducts(prodRes.data);
      setCategories(catRes.data);
      setStockPurchases(purchRes.data);
      setCatalogParts(Array.isArray(partsRes.data) ? partsRes.data : (partsRes.data.parts || []));
      setFutureValue(fvRes.data);
      setStockAlerts(alertRes.data);
    } catch (error) {
      toast.error('Failed to load inventory');
    }
  }, []);

  const fetchFinancials = useCallback(async () => {
    try {
      const tpParams = trueProfitScope === 'year'
        ? { year: new Date().getFullYear() }
        : {};
      const [capRes, salRes, profRes, debtRes, ivsRes] = await Promise.all([
        axios.get(`${API_URL}/api/auto-spares/capital-injections`, { headers }),
        axios.get(`${API_URL}/api/auto-spares/salaries`, { headers }),
        axios.get(`${API_URL}/api/auto-spares/true-profit`, { headers, params: tpParams }),
        axios.get(`${API_URL}/api/auto-spares/debt-repayments`, { headers }),
        axios.get(`${API_URL}/api/auto-spares/admin/investment-vs-sales?months=12`, { headers }),
      ]);
      setCapitalInjections(capRes.data);
      setSalaries(salRes.data);
      setTrueProfit(profRes.data);
      setDebtRepayments(debtRes.data);
      setInvestmentData(ivsRes.data);
    } catch (error) {
      toast.error('Failed to load financials');
    }
  }, [trueProfitScope]);

  useEffect(() => {
    fetchDashboard();
    fetchInventory();
    fetchFinancials();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Re-fetch cash-on-hand when the user switches MTD ↔ YTD
  useEffect(() => {
    fetchFinancials();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [trueProfitScope]);

  // ---- SALESMEN HANDLERS ----
  const handleAddSalesman = async () => {
    if (!newSalesman.full_name || !newSalesman.email || !newSalesman.password) { toast.error('Fill in all required fields'); return; }
    try {
      await axios.post(`${API_URL}/api/auto-spares/admin/salesmen`, newSalesman, { headers });
      toast.success('Salesman added!');
      setNewSalesman({ full_name: '', email: '', password: '', commission_rate: 10, basic_salary: 2000 });
      setShowAddSalesman(false);
      fetchDashboard();
    } catch (error) { toast.error(error.response?.data?.detail || 'Failed'); }
  };

  const handleUpdateSalesman = async () => {
    try {
      await axios.put(`${API_URL}/api/auto-spares/admin/salesmen/${selectedSalesman.id}`, {
        full_name: selectedSalesman.full_name, email: selectedSalesman.email,
        commission_rate: selectedSalesman.commission_rate, basic_salary: selectedSalesman.basic_salary,
        overtime: selectedSalesman.overtime, allowance: selectedSalesman.allowance,
        incentive: selectedSalesman.incentive, incentive_note: selectedSalesman.incentive_note,
        loan_balance: selectedSalesman.loan_balance, loan_deduction: selectedSalesman.loan_deduction,
        target_bonus_rate: selectedSalesman.target_bonus_rate,
        fuel_allowance: selectedSalesman.fuel_allowance,
        fuel_used_flag: !!selectedSalesman.fuel_used_flag,
        fuel_vehicle_reg: selectedSalesman.fuel_vehicle_reg,
      }, { headers });
      toast.success('Updated!');
      setShowEditSalesman(false);
      fetchDashboard();
    } catch (error) { toast.error('Failed'); }
  };

  const handleDeleteSalesman = async (id, name) => {
    if (!window.confirm(`Delete ${name}? This also removes their sales.`)) return;
    try { await axios.delete(`${API_URL}/api/auto-spares/admin/salesmen/${id}`, { headers }); toast.success('Deleted!'); fetchDashboard(); }
    catch { toast.error('Failed'); }
  };

  // ---- SALE HANDLERS ----
  const handleAddSale = async () => {
    if (!saleSalesmanId) { toast.error('Pick a salesman'); return; }
    const customer = (saleCustomerName || '').trim();
    if (!customer) { toast.error('Customer name is required'); return; }
    const cleaned = saleLines.map(l => ({
      part_description: (l.part_description || '').trim(),
      quantity: parseInt(l.quantity, 10) || 1,
      unit_price: parseFloat(l.unit_price) || 0,
      product_id: l.product_id || '',
      master_code: (l.master_code || '').trim(),
      sku: (l.sku || l.master_code || '').trim(),
      idempotency_key: l._key || crypto.randomUUID(),
    })).filter(l => l.part_description && l.unit_price > 0);
    if (!cleaned.length) { toast.error('Add at least one valid line (part + price)'); return; }
    // Build commission_split if toggle on
    let commission_split = [];
    if (splitOn) {
      const coIds = splitSalesmen.map(s => (s || '').trim()).filter(Boolean);
      // Deduplicate and exclude primary if accidentally selected
      const uniqCo = [...new Set(coIds)].filter(id => id !== saleSalesmanId);
      if (uniqCo.length === 0) { toast.error('Select at least one co-salesman for the split'); return; }
      const all = [saleSalesmanId, ...uniqCo];
      if (all.length > 3) { toast.error('Max 3 salesmen per split'); return; }
      commission_split = all.map(id => ({ salesman_id: id })); // equal split — backend computes %
    }
    try {
      await Promise.all(cleaned.map(line =>
        axios.post(`${API_URL}/api/auto-spares/admin/sales`, {
          salesman_id: saleSalesmanId,
          part_description: line.part_description,
          quantity: line.quantity,
          unit_price: line.unit_price,
          product_id: line.product_id || '',
          master_code: line.master_code || '',
          sku: line.sku || '',
          payment_method: salePaymentMethod || 'cash',
          customer_name: customer,
          commission_split,
          idempotency_key: line.idempotency_key,
        }, { headers })
      ));
      toast.success(cleaned.length === 1 ? 'Sale added!' : `${cleaned.length} sale lines added!`);
      setSaleLines([blankSaleLine()]);
      setSaleSalesmanId('');
      setSalePaymentMethod('cash');
      setSaleCustomerName('');
      setSplitOn(false);
      setSplitSalesmen(['']);
      setShowAddSale(false);
      fetchDashboard();
      fetchInventory();
      fetchFinancials();
    } catch (error) { toast.error(error.response?.data?.detail || 'Failed'); }
  };

  // Open the "Split Commission" dialog on an existing sale
  const openSplitDialog = (sale) => {
    setSplitSale(sale);
    const existing = sale.commission_split || [];
    if (existing.length > 0) {
      setSplitDialogRows(existing.map(r => ({ salesman_id: r.salesman_id, percent: r.percent })));
    } else {
      // Default: primary at 100, ready to add co-salesmen
      setSplitDialogRows([{ salesman_id: sale.salesman_id, percent: 100 }]);
    }
    setShowSplitDialog(true);
  };

  const updateSplitRow = (idx, patch) => setSplitDialogRows(prev => prev.map((r, i) => i === idx ? { ...r, ...patch } : r));
  const addSplitRow = () => setSplitDialogRows(prev => {
    if (prev.length >= 3) return prev;
    // Re-distribute equally
    const next = [...prev, { salesman_id: '', percent: 0 }];
    const equal = Math.round((100 / next.length) * 100) / 100;
    return next.map((r, i) => ({ ...r, percent: i === next.length - 1 ? Math.round((100 - equal * (next.length - 1)) * 100) / 100 : equal }));
  });
  const removeSplitRow = (idx) => setSplitDialogRows(prev => {
    if (prev.length <= 1) return prev;
    const next = prev.filter((_, i) => i !== idx);
    const equal = Math.round((100 / next.length) * 100) / 100;
    return next.map((r, i) => ({ ...r, percent: i === next.length - 1 ? Math.round((100 - equal * (next.length - 1)) * 100) / 100 : equal }));
  });

  const saveSplit = async () => {
    if (!splitSale) return;
    // Strip empty rows
    const rows = splitDialogRows
      .filter(r => r.salesman_id)
      .map(r => ({ salesman_id: r.salesman_id, percent: parseFloat(r.percent) || 0 }));
    // If only the primary remains at 100, treat as "clear split"
    const isClear = rows.length === 1 && rows[0].salesman_id === splitSale.salesman_id && Math.abs(rows[0].percent - 100) < 0.5;
    const payload = { commission_split: isClear ? [] : rows };
    try {
      await axios.post(`${API_URL}/api/auto-spares/admin/sales/${splitSale.id}/split-commission`, payload, { headers });
      toast.success(isClear ? 'Split cleared' : 'Split saved');
      setShowSplitDialog(false);
      setSplitSale(null);
      setSplitDialogRows([]);
      fetchDashboard();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save split');
    }
  };

  // Helpers for the multi-line ring-up dialog
  const updateSaleLine = (idx, patch) => setSaleLines(prev => prev.map((l, i) => i === idx ? { ...l, ...patch } : l));
  const addSaleLine = () => setSaleLines(prev => prev.length >= MAX_SALE_LINES ? prev : [...prev, blankSaleLine()]);
  const removeSaleLine = (idx) => setSaleLines(prev => prev.length <= 1 ? [blankSaleLine()] : prev.filter((_, i) => i !== idx));

  const handleEditSale = async () => {
    if (!editingSale) return;
    try {
      await axios.put(`${API_URL}/api/auto-spares/admin/sales/${editingSale.id}`, {
        part_description: editingSale.part_description,
        customer_name: editingSale.customer_name || '',
        quantity: parseInt(editingSale.quantity) || 1,
        unit_price: parseFloat(editingSale.unit_price) || 0
      }, { headers });
      toast.success('Updated!'); setShowEditSale(false); setEditingSale(null); fetchDashboard(); fetchFinancials();
    } catch (error) { toast.error('Failed'); }
  };

  // Sales History search
  const runSalesSearch = async () => {
    setSalesSearchLoading(true);
    try {
      const params = { limit: 5000 };
      if (salesSearchQuery.trim()) params.q = salesSearchQuery.trim();
      if (salesSearchStart) params.start_date = salesSearchStart;
      if (salesSearchEnd) params.end_date = salesSearchEnd;
      const res = await axios.get(`${API_URL}/api/auto-spares/admin/sales-search`, { headers, params });
      setSalesSearchResults(res.data.sales || []);
      setSalesSearchCount(res.data.count || 0);
      setSalesSearchTotalCount(res.data.total_count ?? res.data.count ?? 0);
      setSalesSearchTruncated(!!res.data.truncated);
      setSalesSearchTotal(res.data.total || 0);
      setSalesSearchRan(true);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Search failed');
    } finally {
      setSalesSearchLoading(false);
    }
  };

  const clearSalesSearch = () => {
    setSalesSearchQuery('');
    setSalesSearchStart('');
    setSalesSearchEnd('');
    setSalesSearchResults([]);
    setSalesSearchCount(0);
    setSalesSearchTotal(0);
    setSalesSearchRan(false);
  };

  const handleDeleteSale = async (saleId) => {
    const reason = window.prompt('Cancel this sale? Enter a reason (a reversal is posted and stock is restored):', 'Cancelled');
    if (reason === null) return;
    try { await axios.delete(`${API_URL}/api/auto-spares/admin/sales/${saleId}`, { headers, params: { reason: reason || 'Cancelled' } }); toast.success('Sale cancelled — reversal posted'); fetchDashboard(); fetchFinancials(); }
    catch { toast.error('Failed'); }
  };

  // ---- PRINT INVOICE FROM SALE ----
  const handlePrintInvoiceFromSale = async (saleId) => {
    const customer = window.prompt('Customer name for the invoice (leave blank for "Cash Sale"):', '');
    if (customer === null) return;
    try {
      const res = await axios.post(
        `${API_URL}/api/customer-invoices/from-sale/${saleId}`,
        { customer_name: customer || 'Cash Sale' },
        { headers }
      );
      const { id, invoice_number } = res.data;
      toast.success(`Created ${invoice_number} — opening for print…`);
      const pdfRes = await axios.get(`${API_URL}/api/customer-invoices/${id}/pdf`, { headers, responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([pdfRes.data], { type: 'application/pdf' }));
      const w = window.open(url);
      if (w) setTimeout(() => w.print(), 600);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not create invoice');
    }
  };

  // ---- ISSUE PAYSLIP (auto-decrement loan + reset incentive) ----
  const currentPeriod = () => new Date().toISOString().slice(0, 7); // YYYY-MM
  const periodLabel = (yyyymm) => {
    if (!yyyymm) return '';
    const [y, m] = yyyymm.split('-');
    return new Date(parseInt(y), parseInt(m) - 1, 1).toLocaleString('en-ZA', { month: 'long', year: 'numeric' });
  };

  const handleIssuePayslip = async (salesman) => {
    const period = window.prompt(
      `Issue payslip for ${salesman.full_name} — period (YYYY-MM):`,
      currentPeriod()
    );
    if (!period) return;
    if (!/^\d{4}-\d{2}$/.test(period)) {
      toast.error('Please enter a period like 2026-04');
      return;
    }
    if (!window.confirm(
      `Issue payslip for ${salesman.full_name} for ${periodLabel(period)}?\n\n` +
      `• Loan deduction R${salesman.loan_deduction || 0} will be subtracted from outstanding R${salesman.loan_balance || 0}\n` +
      `• Incentive R${salesman.incentive || 0} will reset to 0 (one-time)\n\n` +
      `Idempotent — won't double-process the same period.`
    )) return;
    try {
      const res = await axios.post(
        `${API_URL}/api/auto-spares/admin/salesmen/${salesman.id}/issue-payslip`,
        { period },
        { headers }
      );
      if (res.data.already_issued) {
        toast.info(`${salesman.full_name} already issued for ${periodLabel(period)}.`);
      } else {
        toast.success(
          `${salesman.full_name}: Net R${res.data.net_pay} issued — loan now R${res.data.loan_balance_after}`
        );
      }
      fetchDashboard();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to issue');
    }
  };

  const handleDownloadPayslip = async (salesman) => {
    const period = window.prompt(
      `Download payslip PDF for ${salesman.full_name} — period (YYYY-MM):`,
      currentPeriod(),
    );
    if (!period) return;
    if (!/^\d{4}-\d{2}$/.test(period)) { toast.error('Please enter a period like 2026-04'); return; }
    try {
      const res = await axios.get(
        `${API_URL}/api/auto-spares/admin/salesmen/${salesman.id}/payslip-pdf`,
        { headers, params: { month: period }, responseType: 'blob' },
      );
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `JSM_Salary_Statement_${(salesman.full_name || 'salesman').replace(/\s+/g, '_')}_${period}.pdf`;
      document.body.appendChild(a); a.click();
      a.remove(); URL.revokeObjectURL(url);
      toast.success(`Downloaded ${salesman.full_name} (${periodLabel(period)})`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Download failed');
    }
  };

  const handleIssueAllPayslips = async () => {
    const period = window.prompt('Issue payslips for ALL salesmen — period (YYYY-MM):', currentPeriod());
    if (!period) return;
    if (!/^\d{4}-\d{2}$/.test(period)) { toast.error('Please enter a period like 2026-04'); return; }
    if (!window.confirm(
      `Issue payslips for ALL salesmen for ${periodLabel(period)}?\n\n` +
      'Loans will be deducted, incentives reset. Salesmen already issued for this period are skipped.'
    )) return;
    try {
      const res = await axios.post(
        `${API_URL}/api/auto-spares/admin/issue-all-payslips`,
        { period },
        { headers }
      );
      toast.success(`Issued ${res.data.issued_count}, skipped ${res.data.skipped_count} (already done).`);
      fetchDashboard();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed');
    }
  };

  // ---- SEND WHATSAPP INVOICE ----
  const handleWhatsAppInvoice = async (sale) => {
    // Collect customer details
    const name = window.prompt('Customer name:', '');
    if (name === null) return;
    let phoneInput = window.prompt('Customer WhatsApp number (e.g. 0821234567):', '');
    if (phoneInput === null) return;
    const digits = (phoneInput || '').replace(/\D/g, '');
    if (digits.length < 9) {
      toast.error('Please enter a valid phone number');
      return;
    }
    // Default to South Africa (+27) if the user typed a local 0-prefixed number
    const waNumber = digits.startsWith('0') ? '27' + digits.slice(1) : digits;

    try {
      const res = await axios.post(
        `${API_URL}/api/customer-invoices/from-sale/${sale.id}`,
        { customer_name: name || 'Cash Sale', customer_phone: phoneInput },
        { headers }
      );
      const { invoice_number, grand_total } = res.data;

      // Fetch company bank details for the payment line
      let bankLine = '';
      try {
        const s = await axios.get(`${API_URL}/api/invoice-settings`, { headers });
        const bits = [];
        if (s.data.bank_name) bits.push(s.data.bank_name);
        if (s.data.bank_account) bits.push(`Acc ${s.data.bank_account}`);
        if (bits.length) bankLine = `\nPay via EFT: ${bits.join(' ')}\nReference: ${invoice_number}`;
      } catch {}

      const totalStr = 'R ' + Number(grand_total || 0).toLocaleString('en-ZA', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
      const msg = `Hi ${name || 'there'},\n\nYour invoice *${invoice_number}* for ${sale.part_description || 'your purchase'} is ready.\nTotal: *${totalStr}*${bankLine}\n\nThank you for your business.\n- JSM Auto Spares`;
      const waUrl = `https://wa.me/${waNumber}?text=${encodeURIComponent(msg)}`;
      window.open(waUrl, '_blank');
      toast.success(`Invoice ${invoice_number} created — WhatsApp opened`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not create invoice');
    }
  };

  // ---- EXPENSE HANDLERS ----
  const handleAddExpense = async () => {
    if (!newExpense.description || !newExpense.amount) { toast.error('Fill description and amount'); return; }
    try {
      await axios.post(`${API_URL}/api/auto-spares/admin/expenses`, newExpense, { headers });
      toast.success('Expense added!');
      setNewExpense({ description: '', amount: '', category: 'General', date: new Date().toISOString().slice(0, 10) });
      setShowAddExpense(false);
      fetchDashboard();
      fetchFinancials();
    } catch (error) { toast.error('Failed'); }
  };

  const handleEditExpense = async () => {
    if (!editingExpense) return;
    try {
      await axios.put(`${API_URL}/api/auto-spares/admin/expenses/${editingExpense.id}`, {
        description: editingExpense.description, amount: parseFloat(editingExpense.amount) || 0,
        category: editingExpense.category, date: editingExpense.date
      }, { headers });
      toast.success('Updated!'); setShowEditExpense(false); setEditingExpense(null); fetchDashboard(); fetchFinancials();
    } catch (error) { toast.error('Failed'); }
  };

  const handleDeleteExpense = async (id) => {
    if (!window.confirm('Delete this expense?')) return;
    try { await axios.delete(`${API_URL}/api/auto-spares/admin/expenses/${id}`, { headers }); toast.success('Deleted!'); fetchDashboard(); fetchFinancials(); }
    catch { toast.error('Failed'); }
  };

  // ---- PRODUCT HANDLERS ----
  const handleAddProduct = async () => {
    if (!newProduct.name || !newProduct.category) { toast.error('Name and category required'); return; }
    try {
      await axios.post(`${API_URL}/api/auto-spares/products`, newProduct, { headers });
      toast.success('Product added!');
      setNewProduct({ name: '', category: '', subcategory: '', cost_price: '', selling_price: '', quantity: 1, vehicle_source: '' });
      setShowAddProduct(false);
      fetchInventory();
      fetchFinancials();
    } catch (error) { toast.error('Failed'); }
  };

  const handleEditProduct = async () => {
    if (!editingProduct) return;
    try {
      await axios.put(`${API_URL}/api/auto-spares/products/${editingProduct.id}`, editingProduct, { headers });
      toast.success('Updated!'); setShowEditProduct(false); setEditingProduct(null); fetchInventory(); fetchFinancials();
    } catch (error) { toast.error('Failed'); }
  };

  const handleDeleteProduct = async (id) => {
    if (!window.confirm('Delete this product?')) return;
    try { await axios.delete(`${API_URL}/api/auto-spares/products/${id}`, { headers }); toast.success('Deleted!'); fetchInventory(); fetchFinancials(); }
    catch { toast.error('Failed'); }
  };

  const handleWipeInventory = async () => {
    if (wipeConfirmText.trim() !== 'DELETE-ALL-INVENTORY') {
      toast.error('Type DELETE-ALL-INVENTORY exactly to confirm');
      return;
    }
    setWiping(true);
    try {
      const res = await axios.delete(`${API_URL}/api/auto-spares/inventory/wipe-all`, {
        headers,
        data: { confirm: 'DELETE-ALL-INVENTORY' },
      });
      toast.success(`Deleted ${res.data.products_deleted} products (${res.data.items_lost} items)`);
      setShowWipeInventory(false);
      setWipeConfirmText('');
      fetchInventory();
      fetchFinancials();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Wipe failed');
    } finally {
      setWiping(false);
    }
  };

  // ---- BULK PURCHASE HANDLER ----
  const handleBulkPurchase = async () => {
    if (!bulkPurchase.description || !bulkPurchase.total_cost || bulkPurchase.items.length === 0) {
      toast.error('Fill description, total cost, and at least one item'); return;
    }
    const validItems = bulkPurchase.items.filter(i => i.name);
    if (validItems.length === 0) { toast.error('Add at least one item with a name'); return; }
    try {
      await axios.post(`${API_URL}/api/auto-spares/stock-purchase`, {
        description: bulkPurchase.description,
        total_cost: parseFloat(bulkPurchase.total_cost),
        items: validItems.map(i => ({
          name: i.name, category: i.category || 'Miscellaneous', subcategory: i.subcategory || '',
          quantity: parseInt(i.quantity) || 1, cost_allocation: parseFloat(i.cost_allocation) || 0
        }))
      }, { headers });
      toast.success('Bulk purchase recorded! Inventory updated with weighted avg costs.');
      setBulkPurchase({ description: '', total_cost: '', items: [{ name: '', category: '', subcategory: '', quantity: 1, cost_allocation: '' }] });
      setShowBulkPurchase(false);
      fetchInventory();
      fetchFinancials();
    } catch (error) { toast.error(error.response?.data?.detail || 'Failed'); }
  };

  const addBulkItem = () => {
    setBulkPurchase(prev => ({
      ...prev,
      items: [...prev.items, { name: '', category: '', subcategory: '', quantity: 1, cost_allocation: '' }]
    }));
  };

  const updateBulkItem = (idx, field, value) => {
    setBulkPurchase(prev => {
      const items = [...prev.items];
      items[idx] = { ...items[idx], [field]: value };
      return { ...prev, items };
    });
  };

  const removeBulkItem = (idx) => {
    setBulkPurchase(prev => ({ ...prev, items: prev.items.filter((_, i) => i !== idx) }));
  };

  // ---- CAPITAL & SALARY HANDLERS ----
  const handleAddCapital = async () => {
    if (!newCapital.description || !newCapital.amount) { toast.error('Fill description and amount'); return; }
    try {
      await axios.post(`${API_URL}/api/auto-spares/capital-injections`, newCapital, { headers });
      toast.success('Capital injection recorded!');
      setNewCapital({ description: '', amount: '', date: new Date().toISOString().slice(0, 10), source: '' });
      setShowAddCapital(false);
      fetchFinancials();
    } catch (error) { toast.error('Failed'); }
  };

  const handleDeleteCapital = async (id) => {
    if (!id) { toast.error('Cannot delete: row has no id (legacy data — refresh and try again)'); return; }
    if (!window.confirm('Delete?')) return;
    try {
      await axios.delete(`${API_URL}/api/auto-spares/capital-injections/${id}`, { headers });
      toast.success('Deleted!');
      fetchFinancials();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Failed to delete');
    }
  };

  const handleAddDebt = async () => {
    if (!newDebt.description || !newDebt.amount) { toast.error('Fill description and amount'); return; }
    try {
      await axios.post(`${API_URL}/api/auto-spares/debt-repayments`, newDebt, { headers });
      toast.success('Debt repayment recorded!');
      setNewDebt({ description: '', amount: '', date: new Date().toISOString().slice(0, 10), paid_to: '' });
      setShowAddDebt(false);
      fetchFinancials();
    } catch { toast.error('Failed'); }
  };

  const handleDeleteDebt = async (id) => {
    if (!window.confirm('Delete?')) return;
    try { await axios.delete(`${API_URL}/api/auto-spares/debt-repayments/${id}`, { headers }); toast.success('Deleted!'); fetchFinancials(); }
    catch { toast.error('Failed'); }
  };

  const handleAddSalary = async () => {
    if (!newSalary.employee_name || !newSalary.amount) { toast.error('Fill employee name and amount'); return; }
    try {
      await axios.post(`${API_URL}/api/auto-spares/salaries`, newSalary, { headers });
      toast.success('Salary recorded!');
      setNewSalary({ employee_name: '', amount: '', date: new Date().toISOString().slice(0, 10), period: '' });
      setShowAddSalary(false);
      fetchFinancials();
    } catch (error) { toast.error('Failed'); }
  };

  const handleDeleteSalary = async (id) => {
    if (!window.confirm('Delete?')) return;
    try { await axios.delete(`${API_URL}/api/auto-spares/salaries/${id}`, { headers }); toast.success('Deleted!'); fetchFinancials(); }
    catch { toast.error('Failed'); }
  };

  // Select product for sale
  const handleSelectProduct = (productId) => {
    const product = products.find(p => p.id === productId);
    if (product) {
      // Add to the first empty/blank line, or append a new one
      setSaleLines(prev => {
        const idx = prev.findIndex(l => !(l.part_description || '').trim());
        const target = idx === -1 ? prev.length : idx;
        const patched = {
          part_description: product.name,
          unit_price: product.selling_price ? String(product.selling_price) : '',
          product_id: productId,
          quantity: 1,
          _search: product.name,
          _open: false,
        };
        if (idx === -1 && prev.length >= MAX_SALE_LINES) return prev;
        const copy = [...prev];
        if (idx === -1) copy.push(patched); else copy[target] = { ...copy[target], ...patched };
        return copy;
      });
    }
  };

  // Derived
  const totalNewSale = saleLines.reduce((s, l) => s + ((parseFloat(l.unit_price) || 0) * (parseInt(l.quantity, 10) || 1)), 0);
  const editSaleTotal = editingSale ? (parseFloat(editingSale.unit_price) || 0) * (parseInt(editingSale.quantity) || 1) : 0;
  // Inventory value = stock-on-hand × LAST actual sales price (from sales history)
  // with graceful fallbacks. `futureValue.total_future_value` comes from
  // /api/auto-spares/products/future-value and is authoritative.
  const inventoryValueAtCost = futureValue?.total_cost_value ?? products.reduce((sum, p) => sum + (p.cost_price || 0) * (p.quantity || 0), 0);
  // "Value of all stock in inventory" = qty on hand × current catalog selling price.
  // We compute this locally (not from the future-value endpoint) so the number
  // always matches what admins see per-row in the Inventory table.
  const inventoryValueRetail = products.reduce(
    (sum, p) => sum + (Number(p.selling_price) || 0) * (Number(p.quantity) || 0),
    0,
  );
  const inventoryValue = inventoryValueAtCost;
  const potentialMargin = inventoryValueRetail - inventoryValueAtCost;
  const totalStockItems = products.reduce((sum, p) => sum + (p.quantity || 0), 0);

  // Get subcategories for selected category
  const getSubcategories = (catName) => {
    const cat = categories.find(c => c.name === catName);
    return cat ? cat.subcategories || [] : [];
  };

  if (loading) return <div className="flex items-center justify-center h-screen text-white">Loading...</div>;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 p-4 md:p-6">
      {/* Header */}
      <div className="relative text-center mb-6">
        <div className="absolute right-0 top-0 flex gap-2">
          <Button
            onClick={() => navigate('/admin')}
            variant="outline"
            className="border-slate-500 text-slate-200 hover:bg-slate-700 hover:text-white"
            title="Back to main menu"
            data-testid="admin-spares-home-btn"
          >
            <Home className="h-4 w-4 mr-2" /> Home
          </Button>
          <Button onClick={handleLogout} variant="outline" className="border-rose-600 text-rose-400 hover:bg-rose-600 hover:text-white" data-testid="logout-btn">
            <LogOut className="h-4 w-4 mr-2" /> Logout
          </Button>
        </div>
        <h1 className="text-3xl font-bold text-white mb-1">JSM AUTO SPARES</h1>
        <p className="text-lg text-amber-400">Admin Dashboard</p>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
        <Card className="bg-gradient-to-br from-emerald-600 to-emerald-700 border-0 text-white">
          <CardContent className="p-3">
            <div className="flex items-center gap-1 mb-1"><TrendingUp className="h-4 w-4" /><span className="text-xs opacity-80">Month Sales</span></div>
            <p className="text-xl font-bold">{formatCurrency(dashboardData?.month_total)}</p>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-orange-600 to-orange-700 border-0 text-white">
          <CardContent className="p-3">
            <div className="flex items-center gap-1 mb-1"><Receipt className="h-4 w-4" /><span className="text-xs opacity-80">Month Expenses</span></div>
            <p className="text-xl font-bold" data-testid="admin-month-expenses">{formatCurrency(expenseTotals.month_total)}</p>
          </CardContent>
        </Card>
        <Popover>
          <PopoverTrigger asChild>
            <Card
              className={`bg-gradient-to-br ${(trueProfit?.cash_on_hand || 0) >= 0 ? 'from-blue-600 to-blue-700' : 'from-red-700 to-red-800'} border-0 text-white cursor-pointer hover:brightness-110 transition`}
              data-testid="cash-on-hand-card"
            >
              <CardContent className="p-3">
                <div className="flex items-center justify-between mb-1">
                  <div className="flex items-center gap-1"><DollarSign className="h-4 w-4" /><span className="text-xs opacity-80">Cash on Hand</span></div>
                  <div className="flex items-center gap-1">
                    <button
                      type="button"
                      onClick={(e) => { e.stopPropagation(); setTrueProfitScope('month'); }}
                      className={`text-[9px] px-1.5 py-0.5 rounded border ${trueProfitScope === 'month' ? 'bg-white/25 border-white/50' : 'border-white/20 opacity-60 hover:opacity-90'}`}
                      data-testid="coh-scope-month"
                    >MTD</button>
                    <button
                      type="button"
                      onClick={(e) => { e.stopPropagation(); setTrueProfitScope('year'); }}
                      className={`text-[9px] px-1.5 py-0.5 rounded border ${trueProfitScope === 'year' ? 'bg-white/25 border-white/50' : 'border-white/20 opacity-60 hover:opacity-90'}`}
                      data-testid="coh-scope-year"
                    >YTD</button>
                  </div>
                </div>
                <p className="text-xl font-bold" data-testid="cash-on-hand">{formatCurrency(trueProfit?.cash_on_hand)}</p>
                <p className="text-[10px] opacity-70">
                  {trueProfit?.period_label || (trueProfitScope === 'year' ? 'This year' : 'This month')} · click for breakdown
                </p>
              </CardContent>
            </Card>
          </PopoverTrigger>
          <PopoverContent className="w-80 p-0" align="end">
            <div className="p-3 border-b bg-slate-50">
              <div className="text-xs uppercase tracking-wide text-slate-600 font-semibold">Cash on Hand Reconciliation</div>
              <div className="text-[10px] text-slate-500">
                Scope: <b>{trueProfit?.period_scope === 'year' ? 'Year-to-date' : 'Month-to-date'}</b>
                {' · '}<span className="font-mono">{trueProfit?.period_label}</span>
              </div>
            </div>
            <div className="p-3 text-sm space-y-1.5">
              <div className="flex justify-between"><span className="text-emerald-700">+ Sales (revenue)</span><span className="font-mono font-semibold">{formatCurrency(trueProfit?.cash_on_hand_breakdown?.revenue)}</span></div>
              <div className="flex justify-between"><span className="text-rose-700">− Operating Expenses</span><span className="font-mono">{formatCurrency(trueProfit?.cash_on_hand_breakdown?.operating_expenses)}</span></div>
              <div className="flex justify-between"><span className="text-rose-700">− Cash Stock Purchases</span><span className="font-mono">{formatCurrency(trueProfit?.cash_on_hand_breakdown?.cash_stock_purchases)}</span></div>
              <div className="flex justify-between"><span className="text-rose-700">− Salaries Paid</span><span className="font-mono">{formatCurrency(trueProfit?.cash_on_hand_breakdown?.salaries_paid)}</span></div>
              <div className="flex justify-between"><span className="text-rose-700">− Debt Repayments (cash out)</span><span className="font-mono">{formatCurrency(trueProfit?.cash_on_hand_breakdown?.debt_repayments_mtd)}</span></div>
              <div className="border-t border-slate-200 mt-2 pt-2 flex justify-between font-bold">
                <span className="text-slate-900">= Calculated Cash on Hand</span>
                <span className={`font-mono ${(trueProfit?.cash_on_hand || 0) >= 0 ? 'text-blue-700' : 'text-rose-700'}`}>{formatCurrency(trueProfit?.cash_on_hand)}</span>
              </div>
            </div>
            <div className="p-3 border-t bg-slate-50 text-[11px] text-slate-600 space-y-1">
              <p><b>How to use:</b> count your physical till and compare. A difference usually means a transaction wasn&apos;t logged — most often a cash pay-out (debt repayment, stock buy, expense).</p>
              <p>Card / EFT / Bank stock purchases are excluded — those don&apos;t touch the till.</p>
            </div>
          </PopoverContent>
        </Popover>
        <Card className={`bg-gradient-to-br ${(trueProfit?.operating_profit || 0) >= 0 ? 'from-teal-600 to-teal-700' : 'from-red-700 to-red-800'} border-0 text-white`}>
          <CardContent className="p-3">
            <div className="flex items-center gap-1 mb-1"><BarChart3 className="h-4 w-4" /><span className="text-xs opacity-80">Operating Profit</span></div>
            <p className="text-xl font-bold" data-testid="operating-profit">{formatCurrency(trueProfit?.operating_profit)}</p>
            <p className="text-[10px] opacity-70">Sales - Expenses - Salaries - Commission</p>
          </CardContent>
        </Card>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-6 gap-3 mb-6">
        <Card className="bg-gradient-to-br from-blue-600 to-blue-700 border-0 text-white">
          <CardContent className="p-3">
            <div className="flex items-center gap-1 mb-1"><Calendar className="h-4 w-4" /><span className="text-xs opacity-80">Today Sales</span></div>
            <p className="text-xl font-bold" data-testid="admin-today-total">{formatCurrency(dashboardData?.today_total)}</p>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-orange-600 to-orange-700 border-0 text-white">
          <CardContent className="p-3">
            <div className="flex items-center gap-1 mb-1"><Receipt className="h-4 w-4" /><span className="text-xs opacity-80">Today Expenses</span></div>
            <p className="text-xl font-bold" data-testid="admin-today-expenses">{formatCurrency(expenseTotals?.today_total)}</p>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-purple-600 to-purple-700 border-0 text-white">
          <CardContent className="p-3">
            <div className="flex items-center gap-1 mb-1"><Users className="h-4 w-4" /><span className="text-xs opacity-80">Salaries + Commission</span></div>
            <p className="text-xl font-bold" data-testid="salary-total">{formatCurrency(trueProfit?.total_salary_cost)}</p>
            <div className="text-[10px] opacity-80 mt-0.5 flex justify-between">
              <span>Basic: <span data-testid="salary-basic">{formatCurrency(trueProfit?.salary_breakdown?.basic_pay ?? trueProfit?.salaries)}</span></span>
              <span>Comm: <span data-testid="salary-commission">{formatCurrency(trueProfit?.salary_breakdown?.commission ?? trueProfit?.commissions)}</span></span>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-cyan-600 to-cyan-700 border-0 text-white">
          <CardContent className="p-3">
            <div className="flex items-center gap-1 mb-1"><Warehouse className="h-4 w-4" /><span className="text-xs opacity-80">Stock Value (@ selling price)</span></div>
            <p className="text-xl font-bold" data-testid="inventory-value-retail">{formatCurrency(inventoryValueRetail)}</p>
            <div className="text-[10px] opacity-80 mt-0.5">qty on hand × selling price</div>
            <div className="mt-1 pt-1 border-t border-white/20 flex items-baseline gap-1">
              <span className="text-[10px] opacity-80">At cost:</span>
              <span className="text-sm font-semibold" data-testid="inventory-value">{formatCurrency(inventoryValue)}</span>
            </div>
            <div className="text-[10px] opacity-80" data-testid="inventory-potential-margin">
              Potential margin: {formatCurrency(potentialMargin)}
            </div>
            {futureValue && futureValue.products_with_cost_fallback > 0 && (
              <div className="text-[9px] opacity-70 mt-0.5 leading-tight" data-testid="future-value-basis">
                {futureValue.products_with_cost_fallback} item(s) still missing a cost — fill via Bulk Cost Import
              </div>
            )}
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-indigo-600 to-indigo-700 border-0 text-white">
          <CardContent className="p-3">
            <div className="flex items-center gap-1 mb-1"><Package className="h-4 w-4" /><span className="text-xs opacity-80">Stock Items</span></div>
            <p className="text-xl font-bold" data-testid="stock-items-count">{totalStockItems}</p>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-amber-600 to-amber-700 border-0 text-white">
          <CardContent className="p-3">
            <div className="flex items-center gap-1 mb-1"><PiggyBank className="h-4 w-4" /><span className="text-xs opacity-80">Capital In</span></div>
            <p className="text-xl font-bold">{formatCurrency(trueProfit?.capital_injections)}</p>
          </CardContent>
        </Card>
        {/* Opening Investment breakdown — PO capital split by purpose */}
        <Card className={`border-0 text-white ${trueProfit?.over_invested ? 'bg-gradient-to-br from-rose-600 to-rose-800' : 'bg-gradient-to-br from-slate-600 to-slate-700'}`} data-testid="opening-investment-card">
          <CardContent className="p-3">
            <div className="flex items-center gap-1 mb-1">
              <TrendingDown className="h-4 w-4" />
              <span className="text-xs opacity-80">Opening Investment</span>
              {trueProfit?.over_invested && (
                <span className="ml-auto text-[9px] uppercase font-bold bg-white/25 rounded px-1" data-testid="over-invested-flag">Over-invested</span>
              )}
            </div>
            <p className="text-xl font-bold" data-testid="opening-investment-total">{formatCurrency(trueProfit?.net_capital_injections)}</p>
            <div className="text-[10px] opacity-80 mt-0.5 grid grid-cols-2 gap-x-2">
              <span>Stock: <span data-testid="opening-stock">{formatCurrency(trueProfit?.capital_injections_by_purpose?.stock)}</span></span>
              <span>Bakkie: <span data-testid="opening-bakkie">{formatCurrency(trueProfit?.capital_injections_by_purpose?.bakkie)}</span></span>
              {(trueProfit?.capital_injections_by_purpose?.unclassified || 0) > 0 && (
                <span className="col-span-2">Other: <span data-testid="opening-other">{formatCurrency(trueProfit?.capital_injections_by_purpose?.unclassified)}</span></span>
              )}
            </div>
            {trueProfit?.over_invested && (
              <div className="text-[10px] mt-1 pt-1 border-t border-white/25 leading-tight" data-testid="over-invested-msg">
                Net capital exceeds operating profit by {formatCurrency(trueProfit?.capital_vs_profit_gap)} — not retaining profit yet.
              </div>
            )}
          </CardContent>
        </Card>

        {/* Low-stock alert tile — Fast movers out of stock or below target */}
        {stockAlerts && stockAlerts.counts && (
          <Card
            onClick={() => setShowStockAlerts(true)}
            className={`cursor-pointer border-0 text-white ${
              stockAlerts.counts.fast_out_of_stock > 0
                ? 'bg-gradient-to-br from-rose-600 to-rose-800 hover:brightness-110 ring-2 ring-rose-400 animate-pulse'
                : stockAlerts.counts.fast_alerts > 0
                  ? 'bg-gradient-to-br from-amber-600 to-amber-700 hover:brightness-110'
                  : stockAlerts.counts.total_alerts > 0
                    ? 'bg-gradient-to-br from-sky-600 to-sky-700 hover:brightness-110'
                    : 'bg-gradient-to-br from-emerald-600 to-emerald-700'
            }`}
            data-testid="stock-alerts-card"
          >
            <CardContent className="p-3">
              <div className="flex items-center gap-1 mb-1">
                <AlertTriangle className="h-4 w-4" />
                <span className="text-xs opacity-80">Low Stock Alerts</span>
              </div>
              <p className="text-xl font-bold" data-testid="stock-alerts-total">
                {stockAlerts.counts.total_alerts}
              </p>
              <div className="text-[10px] opacity-90 mt-0.5 flex justify-between gap-1">
                <span>OOS: <span className="font-semibold" data-testid="stock-alerts-oos">{stockAlerts.counts.out_of_stock}</span></span>
                <span>Below: <span className="font-semibold" data-testid="stock-alerts-below">{stockAlerts.counts.below_target}</span></span>
              </div>
              {stockAlerts.counts.fast_alerts > 0 && (
                <div className="text-[10px] mt-1 pt-1 border-t border-white/25 leading-tight font-semibold" data-testid="stock-alerts-fast">
                  Fast-movers: {stockAlerts.counts.fast_out_of_stock} out · {stockAlerts.counts.fast_below_target} low — click to reorder
                </div>
              )}
            </CardContent>
          </Card>
        )}
      </div>

      {/* Main Tabs */}
      <Tabs defaultValue="sales" className="mb-6">
        <TabsList className="bg-slate-700 mb-4 flex-wrap h-auto gap-1">
          <TabsTrigger value="sales" className="data-[state=active]:bg-emerald-600 text-white" data-testid="sales-tab">Sales</TabsTrigger>
          <TabsTrigger value="cash-up" className="data-[state=active]:bg-blue-600 text-white" data-testid="cash-up-tab">Cash Up</TabsTrigger>
          <TabsTrigger value="expenses-by-month" className="data-[state=active]:bg-orange-700 text-white" data-testid="expenses-by-month-tab">Expenses by Month</TabsTrigger>
          <TabsTrigger value="expenses" className="data-[state=active]:bg-orange-600 text-white" data-testid="expenses-tab">Expenses</TabsTrigger>
          <TabsTrigger value="inventory" className="data-[state=active]:bg-cyan-600 text-white" data-testid="inventory-tab">Inventory</TabsTrigger>
          <TabsTrigger value="cost-import" className="data-[state=active]:bg-emerald-600 text-white" data-testid="cost-import-tab">Bulk Cost Import</TabsTrigger>
          <TabsTrigger value="bulk-purchase" className="data-[state=active]:bg-indigo-600 text-white" data-testid="bulk-purchase-tab">Bulk Purchase</TabsTrigger>
          <TabsTrigger value="financials" className="data-[state=active]:bg-purple-600 text-white" data-testid="financials-tab">Capital & Salaries</TabsTrigger>
          <TabsTrigger value="debt" className="data-[state=active]:bg-red-600 text-white" data-testid="debt-tab">Debt Repayment</TabsTrigger>
          <TabsTrigger value="true-profit" className="data-[state=active]:bg-teal-600 text-white" data-testid="true-profit-tab">True Profit</TabsTrigger>
          <TabsTrigger value="pnl-statement" className="data-[state=active]:bg-emerald-700 text-white" data-testid="pnl-statement-tab">P&amp;L Statement</TabsTrigger>
          <TabsTrigger value="salesmen" className="data-[state=active]:bg-rose-600 text-white" data-testid="salesmen-tab">Salesmen</TabsTrigger>
          <TabsTrigger value="sales-history" className="data-[state=active]:bg-fuchsia-600 text-white" data-testid="sales-history-tab">Sales History</TabsTrigger>
          <TabsTrigger value="parts-catalog" className="data-[state=active]:bg-emerald-600 text-white" data-testid="parts-catalog-tab">Parts Catalog</TabsTrigger>
          <TabsTrigger value="stock-take" className="data-[state=active]:bg-cyan-600 text-white" data-testid="stock-take-tab">Stock Take</TabsTrigger>
          <TabsTrigger value="vehicles-cogs" className="data-[state=active]:bg-emerald-600 text-white" data-testid="vehicles-cogs-tab">Vehicles &amp; COGS</TabsTrigger>
          <TabsTrigger value="invoices" className="data-[state=active]:bg-amber-600 text-white" data-testid="invoices-tab">Invoices</TabsTrigger>
          <TabsTrigger value="procurement" className="data-[state=active]:bg-blue-600 text-white" data-testid="procurement-tab">Procurement</TabsTrigger>
          <TabsTrigger value="expansion-budget" className="data-[state=active]:bg-indigo-600 text-white" data-testid="expansion-budget-tab">Expansion Budget</TabsTrigger>
          <TabsTrigger value="investment" className="data-[state=active]:bg-yellow-600 text-white" data-testid="investment-tab">Investment vs Sales</TabsTrigger>
          <TabsTrigger value="credits" className="data-[state=active]:bg-rose-600 text-white" data-testid="credits-tab">Credits</TabsTrigger>
        </TabsList>

        {/* ========== SALES TAB ========== */}
        <TabsContent value="sales">
          <div className="flex justify-center mb-6">
            <Dialog open={showAddSale} onOpenChange={(o) => { setShowAddSale(o); if (!o) { setSaleLines([blankSaleLine()]); setSaleSalesmanId(''); setSalePaymentMethod('cash'); setSaleCustomerName(''); } }}>
              <DialogTrigger asChild>
                <Button className="bg-emerald-600 hover:bg-emerald-700 text-white px-8 py-3 text-base" data-testid="admin-add-sale-btn">
                  <Plus className="h-5 w-5 mr-2" /> Add Sale
                </Button>
              </DialogTrigger>
              <DialogContent className="bg-slate-800 border-slate-700 text-white max-w-2xl max-h-[90vh] overflow-y-auto">
                <DialogHeader><DialogTitle className="flex items-center gap-2"><Package className="h-5 w-5" /> ADD SALE <span className="text-xs text-slate-400 font-normal">(up to {MAX_SALE_LINES} lines)</span></DialogTitle></DialogHeader>
                <div className="space-y-3">
                  <div>
                    <Label>Select Salesman *</Label>
                    <Select value={saleSalesmanId} onValueChange={setSaleSalesmanId}>
                      <SelectTrigger className="bg-slate-700 border-slate-600" data-testid="sale-salesman-select"><SelectValue placeholder="Choose salesman..." /></SelectTrigger>
                      <SelectContent className="bg-slate-700 border-slate-600">
                        {salesmen.map((s) => <SelectItem key={s.id} value={s.id}>{s.full_name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <Label>Customer Name *</Label>
                    <Input
                      value={saleCustomerName}
                      onChange={(e) => setSaleCustomerName(e.target.value)}
                      placeholder="e.g., John Smith, Walk-in, ABC Motors"
                      className="bg-slate-700 border-slate-600"
                      data-testid="sale-customer-name-input"
                    />
                  </div>

                  {saleLines.map((line, idx) => (
                    <div key={idx} className="rounded-lg border border-slate-700 bg-slate-900/40 p-3 space-y-2" data-testid={`admin-sale-line-${idx}`}>
                      <div className="flex items-center justify-between">
                        <span className="text-xs font-bold text-slate-400">Line {idx + 1}</span>
                        {saleLines.length > 1 && (
                          <button type="button" onClick={() => removeSaleLine(idx)} className="text-rose-400 hover:text-rose-300 text-xs" data-testid={`admin-sale-line-remove-${idx}`}>Remove</button>
                        )}
                      </div>
                      <div className="relative">
                        <Label className="text-xs">Search Catalog</Label>
                        <Input
                          value={line._search || ''}
                          onFocus={() => updateSaleLine(idx, { _open: true })}
                          onChange={(e) => updateSaleLine(idx, { _search: e.target.value, _open: true })}
                          placeholder="Type 'BOD007', 'Door', 'Alternator', 'BRK', etc."
                          className="bg-slate-700 border-slate-600 h-9"
                          data-testid={`sale-catalog-search-input-${idx}`}
                          autoComplete="off"
                        />
                        {line._open && (line._search || '').trim().length > 0 && (() => {
                          const q = (line._search || '').trim().toLowerCase();
                          const matches = catalogParts.filter(p =>
                            (p.sku || '').toLowerCase().includes(q) ||
                            (p.name || '').toLowerCase().includes(q) ||
                            (p.category || '').toLowerCase().includes(q)
                          ).slice(0, 30);
                          return (
                            <div className="absolute top-full left-0 right-0 z-30 mt-1 bg-slate-800 border border-slate-600 rounded-lg shadow-xl max-h-64 overflow-y-auto">
                              {matches.length === 0 ? (
                                <div className="px-3 py-3 text-slate-400 text-sm text-center">No matches — type the description below manually.</div>
                              ) : matches.map(p => (
                                <button
                                  key={p.id}
                                  type="button"
                                  onClick={() => updateSaleLine(idx, {
                                    part_description: `${p.master_code || p.sku} — ${p.name}`,
                                    master_code: p.master_code || p.sku,
                                    sku: p.master_code || p.sku,
                                    unit_price: p.sale_price ? String(p.sale_price) : '',
                                    product_id: '',
                                    _search: `${p.master_code || p.sku} — ${p.name}`,
                                    _open: false,
                                  })}
                                  className="w-full text-left px-3 py-2 hover:bg-slate-700 flex items-center gap-2 border-b border-slate-700 last:border-0"
                                >
                                  <div className="flex-1 min-w-0">
                                    <div className="font-mono text-[11px] text-amber-300 font-bold">{p.master_code || p.sku}</div>
                                    <div className="text-sm text-white truncate">{p.name}</div>
                                    <div className="text-[10px] text-slate-500">{p.category}{p.subcategory ? ` • ${p.subcategory}` : ''}</div>
                                  </div>
                                  <div className="text-emerald-400 font-bold text-sm shrink-0">{formatCurrency(p.sale_price)}</div>
                                </button>
                              ))}
                            </div>
                          );
                        })()}
                      </div>
                      <div>
                        <Label className="text-xs">Part Description *</Label>
                        <Input value={line.part_description} onChange={(e) => updateSaleLine(idx, { part_description: e.target.value })} placeholder="e.g., Tail Light" className="bg-slate-700 border-slate-600 h-9" data-testid={`sale-part-input-${idx}`} />
                      </div>
                      <div className="grid grid-cols-3 gap-2">
                        <div><Label className="text-xs">Qty</Label><Input type="number" min="1" value={line.quantity} onChange={(e) => updateSaleLine(idx, { quantity: e.target.value })} className="bg-slate-700 border-slate-600 h-9" /></div>
                        <div><Label className="text-xs">Price (R) *</Label><Input type="number" step="0.01" value={line.unit_price} onChange={(e) => updateSaleLine(idx, { unit_price: e.target.value })} placeholder="0.00" className="bg-slate-700 border-slate-600 h-9" data-testid={`sale-price-input-${idx}`} /></div>
                        <div>
                          <Label className="text-xs">Line Total</Label>
                          <div className="h-9 px-3 flex items-center rounded-md bg-slate-700/60 border border-slate-600 text-emerald-400 font-semibold text-sm">
                            {formatCurrency((parseFloat(line.unit_price) || 0) * (parseInt(line.quantity, 10) || 1))}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}

                  <Button
                    type="button"
                    onClick={addSaleLine}
                    disabled={saleLines.length >= MAX_SALE_LINES}
                    className="w-full bg-slate-700 hover:bg-slate-600 disabled:opacity-50 disabled:cursor-not-allowed"
                    data-testid="admin-add-sale-line-btn"
                  >
                    <Plus className="h-4 w-4 mr-1" />
                    {saleLines.length >= MAX_SALE_LINES ? `Max ${MAX_SALE_LINES} lines reached` : `Add another line (${saleLines.length}/${MAX_SALE_LINES})`}
                  </Button>

                  <div>
                    <Label>Payment Method *</Label>
                    <div className="grid grid-cols-4 gap-2 mt-1">
                      {[
                        { v: 'cash',   label: 'Cash',   cls: 'bg-emerald-600 hover:bg-emerald-700 border-emerald-500' },
                        { v: 'online', label: 'Online', cls: 'bg-blue-600 hover:bg-blue-700 border-blue-500' },
                        { v: 'eft',    label: 'EFT',    cls: 'bg-purple-600 hover:bg-purple-700 border-purple-500' },
                        { v: 'card',   label: 'Card',   cls: 'bg-amber-600 hover:bg-amber-700 border-amber-500' },
                        { v: 'credit', label: 'Credit', cls: 'bg-rose-600 hover:bg-rose-700 border-rose-500' },
                      ].map(opt => (
                        <button
                          key={opt.v}
                          type="button"
                          onClick={() => setSalePaymentMethod(opt.v)}
                          data-testid={`sale-pm-${opt.v}`}
                          className={`rounded-md border px-2 py-2 text-sm font-semibold text-white transition ${salePaymentMethod === opt.v ? opt.cls + ' ring-2 ring-white/40' : 'bg-slate-700 border-slate-600 hover:bg-slate-600'}`}
                        >
                          {opt.label}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Split Commission */}
                  <div className="rounded-lg border border-fuchsia-700/40 bg-fuchsia-950/20 p-3">
                    <label className="flex items-start gap-3 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={splitOn}
                        onChange={(e) => { setSplitOn(e.target.checked); if (!e.target.checked) setSplitSalesmen(['']); }}
                        className="mt-1 h-4 w-4 accent-fuchsia-500"
                        data-testid="split-commission-toggle"
                      />
                      <div className="flex-1">
                        <p className="text-sm font-semibold text-white">Split Commission</p>
                        <p className="text-[11px] text-slate-400 mt-0.5">
                          Equal split among everyone. Sale stays on primary's history; co-salesmen share commission only.
                        </p>
                      </div>
                    </label>
                    {splitOn && (
                      <div className="mt-3 space-y-2" data-testid="split-co-salesmen-area">
                        {splitSalesmen.map((cid, idx) => (
                          <div key={idx} className="flex items-center gap-2">
                            <span className="text-xs text-fuchsia-300 font-bold w-20">Co {idx + 1}</span>
                            <Select value={cid} onValueChange={(v) => setSplitSalesmen(prev => prev.map((x, i) => i === idx ? v : x))}>
                              <SelectTrigger className="bg-slate-700 border-slate-600 flex-1" data-testid={`split-co-select-${idx}`}>
                                <SelectValue placeholder="Choose co-salesman…" />
                              </SelectTrigger>
                              <SelectContent className="bg-slate-700 border-slate-600">
                                {salesmen.filter(sm => sm.id !== saleSalesmanId).map(sm => (
                                  <SelectItem key={sm.id} value={sm.id}>{sm.full_name}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                            {splitSalesmen.length > 1 && (
                              <button
                                type="button"
                                onClick={() => setSplitSalesmen(prev => prev.filter((_, i) => i !== idx))}
                                className="text-rose-400 hover:text-rose-300 text-xs"
                                data-testid={`split-co-remove-${idx}`}
                              >Remove</button>
                            )}
                          </div>
                        ))}
                        {splitSalesmen.length < 2 && (
                          <Button
                            type="button"
                            onClick={() => setSplitSalesmen(prev => [...prev, ''])}
                            className="w-full bg-fuchsia-700/40 hover:bg-fuchsia-700/60 text-fuchsia-200 text-xs"
                            data-testid="split-add-co-btn"
                          >+ Add another co-salesman (max 3 total)</Button>
                        )}
                        <p className="text-[11px] text-fuchsia-300 mt-1">
                          Split will be equal: <strong>{(100 / (1 + splitSalesmen.filter(Boolean).length)).toFixed(2)}%</strong> each.
                        </p>
                      </div>
                    )}
                  </div>
                  <div className="bg-slate-700 p-3 rounded-lg flex items-center justify-between"><p className="text-sm text-slate-400">GRAND TOTAL ({saleLines.length} {saleLines.length === 1 ? 'line' : 'lines'}):</p><p className="text-2xl font-bold text-emerald-400">{formatCurrency(totalNewSale)}</p></div>
                  <Button onClick={handleAddSale} className="w-full bg-emerald-600 hover:bg-emerald-700" data-testid="save-sale-btn">SAVE SALE</Button>
                </div>
              </DialogContent>
            </Dialog>
          </div>

          <Card className="bg-slate-800/50 border-slate-700">
            <CardHeader><CardTitle className="text-white flex items-center gap-2"><Calendar className="h-5 w-5 text-blue-400" /> TODAY'S SALES</CardTitle></CardHeader>
            <CardContent>
              {todaySales.length === 0 ? <p className="text-slate-400 text-center py-8">No sales today.</p> : (
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader><TableRow className="border-slate-700">
                      <TableHead className="text-slate-400">Salesman</TableHead><TableHead className="text-slate-400">Customer</TableHead><TableHead className="text-slate-400">Part</TableHead>
                      <TableHead className="text-slate-400">Category</TableHead>
                      <TableHead className="text-slate-400 text-right">Qty</TableHead><TableHead className="text-slate-400 text-right">Price</TableHead>
                      <TableHead className="text-slate-400 text-right">Cost</TableHead><TableHead className="text-slate-400 text-right">Total</TableHead>
                      <TableHead className="text-slate-400 text-right">Actions</TableHead>
                    </TableRow></TableHeader>
                    <TableBody>
                      {todaySales.map((sale) => (
                        <TableRow key={sale.id} className="border-slate-700">
                          <TableCell className="text-amber-400 font-medium">
                            {sale.salesman_name}
                            {sale.commission_split && sale.commission_split.length > 0 && (
                              <div className="text-[10px] text-fuchsia-300 mt-0.5" data-testid={`sale-split-badge-${sale.id}`}>
                                Split: {sale.commission_split.map(s => `${(s.salesman_name || '').split(' ')[0]} ${s.percent}%`).join(' / ')}
                              </div>
                            )}
                          </TableCell>
                          <TableCell className="text-cyan-400 text-sm">{sale.customer_name || '-'}</TableCell>
                          <TableCell className="text-white">{sale.part_description}</TableCell>
                          <TableCell className="text-slate-400 text-sm">{sale.category || '-'}</TableCell>
                          <TableCell className="text-white text-right">{sale.quantity}</TableCell>
                          <TableCell className="text-white text-right">{formatCurrency(sale.unit_price)}</TableCell>
                          <TableCell className="text-rose-400 text-right">{sale.cost_total ? formatCurrency(sale.cost_total) : '-'}</TableCell>
                          <TableCell className="text-emerald-400 text-right font-bold">{formatCurrency(sale.total)}</TableCell>
                          <TableCell className="text-right">
                            <div className="flex gap-1 justify-end">
                              <Button size="sm" className="bg-amber-600 hover:bg-amber-700 text-white h-7 px-2" onClick={() => handlePrintInvoiceFromSale(sale.id)} title="Print invoice" data-testid={`print-invoice-sale-${sale.id}`}>
                                <Printer className="h-3 w-3" />
                              </Button>
                              <Button size="sm" className="bg-green-600 hover:bg-green-700 text-white h-7 px-2" onClick={() => handleWhatsAppInvoice(sale)} title="Send invoice via WhatsApp" data-testid={`whatsapp-invoice-sale-${sale.id}`}>
                                <MessageCircle className="h-3 w-3" />
                              </Button>
                              <Button size="sm" className="bg-blue-600 hover:bg-blue-700 text-white h-7 px-2" onClick={() => { setEditingSale({...sale}); setShowEditSale(true); }}><Pencil className="h-3 w-3" /></Button>
                              <Button size="sm" className="bg-fuchsia-600 hover:bg-fuchsia-700 text-white h-7 px-2" onClick={() => openSplitDialog(sale)} title="Split commission" data-testid={`split-sale-${sale.id}`}><Users className="h-3 w-3" /></Button>
                              <Button size="sm" className="bg-rose-600 hover:bg-rose-700 text-white h-7 px-2" onClick={() => handleDeleteSale(sale.id)}><Trash2 className="h-3 w-3" /></Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                      <TableRow className="border-slate-700 bg-slate-700/50">
                        <TableCell colSpan={7} className="text-white font-bold">TOTAL:</TableCell>
                        <TableCell className="text-emerald-400 text-right font-bold text-lg">{formatCurrency(todaySales.reduce((s, x) => s + (x.total || 0), 0))}</TableCell>
                        <TableCell />
                      </TableRow>
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Monthly Sales by Salesman */}
          <Card className="bg-slate-800/50 border-slate-700 mt-6">
            <CardHeader><CardTitle className="text-white flex items-center gap-2"><TrendingUp className="h-5 w-5 text-emerald-400" /> MONTHLY SALES BY SALESMAN</CardTitle></CardHeader>
            <CardContent>
              <Table>
                <TableHeader><TableRow className="border-slate-700">
                  <TableHead className="text-slate-400">Salesman</TableHead><TableHead className="text-slate-400 text-right">Sales</TableHead>
                  <TableHead className="text-slate-400 text-right">Commission</TableHead><TableHead className="text-slate-400 text-right">Target</TableHead>
                  <TableHead className="text-slate-400 text-right">Progress</TableHead>
                </TableRow></TableHeader>
                <TableBody>
                  {dashboardData?.month_by_salesman?.map((item, idx) => {
                    const progress = item.target > 0 ? Math.min(100, (item.total / item.target) * 100) : 0;
                    return (
                      <TableRow key={idx} className="border-slate-700">
                        <TableCell className="text-white font-medium">{item.name}</TableCell>
                        <TableCell className="text-emerald-400 text-right font-bold">{formatCurrency(item.total)}</TableCell>
                        <TableCell className="text-amber-400 text-right">{formatCurrency(item.commission)}</TableCell>
                        <TableCell className="text-slate-400 text-right">{formatCurrency(item.target)}</TableCell>
                        <TableCell className="text-right">{item.target > 0 ? <span className={progress >= 100 ? 'text-emerald-400' : 'text-amber-400'}>{progress.toFixed(0)}%</span> : '-'}</TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* ========== CASH UP TAB ========== */}
        <TabsContent value="cash-up">
          <CashUpPanel />
        </TabsContent>

        {/* ========== EXPENSES BY MONTH TAB ========== */}
        <TabsContent value="expenses-by-month">
          <ExpensesByMonthPanel />
        </TabsContent>

        {/* ========== EXPENSES TAB ========== */}
        <TabsContent value="expenses">
          {/* YTD Expenses Analytics — where the money goes this year */}
          {expensesYtd && expensesYtd.count > 0 && (
            <Card className="bg-slate-800/60 border-slate-700 mb-4" data-testid="ytd-expenses-card">
              <CardHeader className="pb-2 flex flex-row items-center justify-between">
                <CardTitle className="text-white text-base flex items-center gap-2">
                  <Receipt className="h-5 w-5 text-amber-400" />
                  YTD EXPENSES ({expensesYtd.year}) — where the money goes
                </CardTitle>
                <div className="flex items-center gap-3">
                  <div className="text-right">
                    <div className="text-[10px] uppercase tracking-wider text-slate-400">Total YTD</div>
                    <div className="text-xl font-extrabold text-rose-400" data-testid="ytd-total">{formatCurrency(expensesYtd.ytd_total)}</div>
                  </div>
                  <Button size="sm" variant="ghost" onClick={() => setShowYtd((s) => !s)} className="text-slate-300 hover:text-white" data-testid="ytd-toggle">
                    {showYtd ? 'Hide' : 'Show'}
                  </Button>
                </div>
              </CardHeader>
              {showYtd && (
                <CardContent className="space-y-4">
                  {/* Category breakdown — bars */}
                  <div>
                    <div className="text-[10px] uppercase tracking-wider text-slate-400 mb-2 font-semibold">By category · {expensesYtd.by_category.length} buckets · {expensesYtd.count} entries</div>
                    <div className="space-y-1.5">
                      {expensesYtd.by_category.map((c) => (
                        <div key={c.category} className="grid grid-cols-12 gap-2 items-center" data-testid={`ytd-cat-${c.category}`}>
                          <div className="col-span-3 text-white text-sm truncate">{c.category}</div>
                          <div className="col-span-6">
                            <div className="h-2 bg-slate-700 rounded overflow-hidden">
                              <div className="h-full bg-amber-500" style={{ width: `${Math.max(c.percent, 1)}%` }} />
                            </div>
                          </div>
                          <div className="col-span-2 text-right text-rose-400 font-mono text-sm">{formatCurrency(c.total)}</div>
                          <div className="col-span-1 text-right text-slate-400 text-xs">{c.percent}%</div>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Monthly trend */}
                  {expensesYtd.by_month.length > 0 && (
                    <div>
                      <div className="text-[10px] uppercase tracking-wider text-slate-400 mb-2 font-semibold">By month</div>
                      <div className="grid grid-cols-2 sm:grid-cols-4 md:grid-cols-6 gap-2">
                        {expensesYtd.by_month.map((m) => (
                          <div key={m.month} className="rounded border border-slate-700 bg-slate-900/50 p-2 text-center" data-testid={`ytd-month-${m.month}`}>
                            <div className="text-[10px] uppercase tracking-wider text-slate-400 font-mono">{m.month}</div>
                            <div className="text-sm text-rose-400 font-bold">{formatCurrency(m.total)}</div>
                            <div className="text-[10px] text-slate-500">{m.count} entries</div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </CardContent>
              )}
            </Card>
          )}

          <div className="flex justify-center mb-6">
            <Dialog open={showAddExpense} onOpenChange={setShowAddExpense}>
              <DialogTrigger asChild>
                <Button className="bg-orange-600 hover:bg-orange-700 text-white px-8 py-3 text-base" data-testid="add-expense-btn">
                  <Plus className="h-5 w-5 mr-2" /> Add Expense
                </Button>
              </DialogTrigger>
              <DialogContent className="bg-slate-800 border-slate-700 text-white">
                <DialogHeader><DialogTitle className="flex items-center gap-2"><Receipt className="h-5 w-5" /> ADD EXPENSE</DialogTitle></DialogHeader>
                <div className="space-y-4">
                  <div><Label>Description *</Label><Input value={newExpense.description} onChange={(e) => setNewExpense({...newExpense, description: e.target.value})} placeholder="e.g., Fuel" className="bg-slate-700 border-slate-600" data-testid="expense-description-input" /></div>
                  <div className="grid grid-cols-2 gap-4">
                    <div><Label>Amount (R) *</Label><Input type="number" step="0.01" value={newExpense.amount} onChange={(e) => setNewExpense({...newExpense, amount: e.target.value})} placeholder="0.00" className="bg-slate-700 border-slate-600" data-testid="expense-amount-input" /></div>
                    <div><Label>Date</Label><Input type="date" value={newExpense.date} onChange={(e) => setNewExpense({...newExpense, date: e.target.value})} className="bg-slate-700 border-slate-600" /></div>
                  </div>
                  <div>
                    <Label>Category</Label>
                    <Select value={newExpense.category} onValueChange={(val) => setNewExpense({...newExpense, category: val})}>
                      <SelectTrigger className="bg-slate-700 border-slate-600"><SelectValue /></SelectTrigger>
                      <SelectContent className="bg-slate-700 border-slate-600">{EXPENSE_CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <Button onClick={handleAddExpense} className="w-full bg-orange-600 hover:bg-orange-700" data-testid="save-expense-btn">SAVE EXPENSE</Button>
                </div>
              </DialogContent>
            </Dialog>
          </div>

          <Card className="bg-slate-800/50 border-slate-700">
            <CardHeader><CardTitle className="text-white flex items-center gap-2"><Receipt className="h-5 w-5 text-orange-400" /> THIS MONTH'S EXPENSES</CardTitle></CardHeader>
            <CardContent>
              {expenses.length === 0 ? <p className="text-slate-400 text-center py-8">No expenses this month.</p> : (
                <Table>
                  <TableHeader><TableRow className="border-slate-700">
                    <TableHead className="text-slate-400">Date</TableHead><TableHead className="text-slate-400">Description</TableHead>
                    <TableHead className="text-slate-400">Category</TableHead><TableHead className="text-slate-400 text-right">Amount</TableHead>
                    <TableHead className="text-slate-400 text-right">Actions</TableHead>
                  </TableRow></TableHeader>
                  <TableBody>
                    {expenses.map((exp) => (
                      <TableRow key={exp.id} className="border-slate-700">
                        <TableCell className="text-slate-300">{new Date(exp.date).toLocaleDateString()}</TableCell>
                        <TableCell className="text-white font-medium">{exp.description}</TableCell>
                        <TableCell><span className="bg-orange-600/20 text-orange-400 px-2 py-1 rounded text-sm">{exp.category}</span></TableCell>
                        <TableCell className="text-rose-400 text-right font-bold">{formatCurrency(exp.amount)}</TableCell>
                        <TableCell className="text-right">
                          <div className="flex gap-1 justify-end">
                            <Button size="sm" className="bg-blue-600 hover:bg-blue-700 text-white h-7 px-2" onClick={() => { setEditingExpense({...exp}); setShowEditExpense(true); }}><Pencil className="h-3 w-3" /></Button>
                            <Button size="sm" className="bg-rose-600 hover:bg-rose-700 text-white h-7 px-2" onClick={() => handleDeleteExpense(exp.id)}><Trash2 className="h-3 w-3" /></Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                    <TableRow className="border-slate-700 bg-slate-700/50">
                      <TableCell colSpan={3} className="text-white font-bold">MONTH TOTAL:</TableCell>
                      <TableCell className="text-rose-400 text-right font-bold text-lg">{formatCurrency(expenseTotals.month_total)}</TableCell>
                      <TableCell />
                    </TableRow>
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* ========== INVENTORY TAB ========== */}
        <TabsContent value="inventory">
          <div className="flex flex-wrap justify-center gap-3 mb-6">
            <Dialog open={showAddProduct} onOpenChange={setShowAddProduct}>
              <DialogTrigger asChild>
                <Button className="bg-cyan-600 hover:bg-cyan-700 text-white px-8 py-3 text-base" data-testid="add-product-btn">
                  <Plus className="h-5 w-5 mr-2" /> Add Product
                </Button>
              </DialogTrigger>
              <DialogContent className="bg-slate-800 border-slate-700 text-white max-w-lg">
                <DialogHeader><DialogTitle className="flex items-center gap-2"><Package className="h-5 w-5" /> ADD PRODUCT</DialogTitle></DialogHeader>
                <div className="space-y-4">
                  <div><Label>Name *</Label><Input value={newProduct.name} onChange={(e) => setNewProduct({...newProduct, name: e.target.value})} placeholder="e.g., Headlight Left" className="bg-slate-700 border-slate-600" data-testid="product-name-input" /></div>
                  <div>
                    <Label>Category *</Label>
                    <Select value={newProduct.category} onValueChange={(val) => setNewProduct({...newProduct, category: val, subcategory: ''})}>
                      <SelectTrigger className="bg-slate-700 border-slate-600" data-testid="product-category-select"><SelectValue placeholder="Select..." /></SelectTrigger>
                      <SelectContent className="bg-slate-700 border-slate-600 max-h-60">
                        {categories.map((c) => <SelectItem key={c.name} value={c.name}>{c.name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  {newProduct.category && getSubcategories(newProduct.category).length > 0 && (
                    <div>
                      <Label>Subcategory</Label>
                      <Select value={newProduct.subcategory} onValueChange={(val) => setNewProduct({...newProduct, subcategory: val})}>
                        <SelectTrigger className="bg-slate-700 border-slate-600"><SelectValue placeholder="Select..." /></SelectTrigger>
                        <SelectContent className="bg-slate-700 border-slate-600 max-h-60">
                          {getSubcategories(newProduct.category).map((sc) => <SelectItem key={sc} value={sc}>{sc}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                  <div className="grid grid-cols-3 gap-3">
                    <div><Label>Cost Price (R)</Label><Input type="number" step="0.01" value={newProduct.cost_price} onChange={(e) => setNewProduct({...newProduct, cost_price: e.target.value})} placeholder="0.00" className="bg-slate-700 border-slate-600" data-testid="product-cost-input" /></div>
                    <div><Label>Sell Price (R)</Label><Input type="number" step="0.01" value={newProduct.selling_price} onChange={(e) => setNewProduct({...newProduct, selling_price: e.target.value})} placeholder="0.00" className="bg-slate-700 border-slate-600" /></div>
                    <div><Label>Quantity</Label><Input type="number" min="0" value={newProduct.quantity} onChange={(e) => setNewProduct({...newProduct, quantity: e.target.value})} className="bg-slate-700 border-slate-600" /></div>
                  </div>
                  <div><Label>Vehicle Source</Label><Input value={newProduct.vehicle_source} onChange={(e) => setNewProduct({...newProduct, vehicle_source: e.target.value})} placeholder="e.g., Toyota Hilux 2018" className="bg-slate-700 border-slate-600" /></div>
                  <Button onClick={handleAddProduct} className="w-full bg-cyan-600 hover:bg-cyan-700" data-testid="save-product-btn">ADD PRODUCT</Button>
                </div>
              </DialogContent>
            </Dialog>

            <XlsxImportExport
              exportPath="/api/auto-spares/products/export-xlsx"
              importPath="/api/auto-spares/products/import-xlsx"
              label="Inventory"
              testIdPrefix="inventory"
              onImported={fetchInventory}
            />
            <Button
              onClick={() => { setWipeConfirmText(''); setShowWipeInventory(true); }}
              variant="outline"
              className="border-rose-600 text-rose-300 hover:bg-rose-600/20 px-6 py-3 text-base"
              data-testid="wipe-inventory-btn"
              disabled={products.length === 0}
              title={products.length === 0 ? 'Inventory is already empty' : 'Delete every product from inventory'}
            >
              <Trash2 className="h-4 w-4 mr-2" /> Delete All
            </Button>
          </div>

          <Dialog open={showWipeInventory} onOpenChange={(o) => { setShowWipeInventory(o); if (!o) setWipeConfirmText(''); }}>
            <DialogContent className="bg-slate-800 border-rose-700 text-white max-w-md" data-testid="wipe-inventory-dialog">
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2 text-rose-300">
                  <AlertTriangle className="h-5 w-5" /> Delete All Inventory
                </DialogTitle>
              </DialogHeader>
              <div className="space-y-3">
                <div className="bg-rose-950/40 border border-rose-700 rounded p-3 text-sm">
                  <p className="text-rose-200 font-semibold mb-1">
                    You are about to permanently delete <span className="text-white">{products.length} products</span> ({totalStockItems} items on hand).
                  </p>
                  <ul className="text-rose-300 text-xs list-disc ml-5 space-y-0.5">
                    <li>Every product record will be removed</li>
                    <li>Past sales, invoices and PDFs stay intact</li>
                    <li>Selling prices, cost prices and stock take counts will be lost</li>
                    <li>You can re-seed via Bulk Import or Add Product afterwards</li>
                  </ul>
                </div>
                <div>
                  <Label className="text-slate-300 text-sm">Type <span className="font-mono text-rose-300">DELETE-ALL-INVENTORY</span> to confirm:</Label>
                  <Input
                    value={wipeConfirmText}
                    onChange={(e) => setWipeConfirmText(e.target.value)}
                    placeholder="DELETE-ALL-INVENTORY"
                    className="bg-slate-900 border-slate-600 text-white mt-1 font-mono"
                    data-testid="wipe-inventory-confirm-input"
                    autoFocus
                  />
                </div>
              </div>
              <DialogFooter className="flex flex-row gap-2 justify-end">
                <Button
                  variant="outline"
                  onClick={() => setShowWipeInventory(false)}
                  className="bg-slate-700 border-slate-600 text-white hover:bg-slate-600"
                  data-testid="wipe-inventory-cancel-btn"
                >Cancel</Button>
                <Button
                  onClick={handleWipeInventory}
                  disabled={wiping || wipeConfirmText.trim() !== 'DELETE-ALL-INVENTORY'}
                  className="bg-rose-600 hover:bg-rose-700 text-white disabled:opacity-40"
                  data-testid="wipe-inventory-confirm-btn"
                >
                  <Trash2 className="h-4 w-4 mr-1" /> {wiping ? 'Deleting…' : 'Delete Everything'}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>

          <Card className="bg-slate-800/50 border-slate-700">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                <Warehouse className="h-5 w-5 text-cyan-400" /> INVENTORY ({products.length} products, {totalStockItems} items)
              </CardTitle>
            </CardHeader>
            <CardContent>
              {products.length === 0 ? <p className="text-slate-400 text-center py-8">No products in inventory. Add products or do a bulk purchase.</p> : (
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader><TableRow className="border-slate-700">
                      <TableHead className="text-slate-400">Name</TableHead>
                      <TableHead className="text-slate-400">Category</TableHead>
                      <TableHead className="text-slate-400 text-right">Cost</TableHead>
                      <TableHead className="text-slate-400 text-right">Sell</TableHead>
                      <TableHead className="text-slate-400 text-right">Qty</TableHead>
                      <TableHead className="text-slate-400 text-center">Target</TableHead>
                      <TableHead className="text-slate-400 text-center">Priority</TableHead>
                      <TableHead className="text-slate-400 text-right">Cost Value</TableHead>
                      <TableHead className="text-slate-400 text-right">Sell Value</TableHead>
                      <TableHead className="text-slate-400">Source</TableHead>
                      <TableHead className="text-slate-400 text-right">Actions</TableHead>
                    </TableRow></TableHeader>
                    <TableBody>
                      {products.map((p) => (
                        <TableRow key={p.id} className={`border-slate-700 ${p.quantity === 0 ? 'opacity-50' : ''}`}>
                          <TableCell className="text-white font-medium">{p.name}</TableCell>
                          <TableCell className="text-slate-400 text-sm">{p.category}{p.subcategory ? ` > ${p.subcategory}` : ''}</TableCell>
                          <TableCell className="text-rose-400 text-right">{formatCurrency(p.cost_price)}</TableCell>
                          <TableCell className="text-emerald-400 text-right">{formatCurrency(p.selling_price)}</TableCell>
                          <TableCell className={`text-right font-bold ${p.quantity === 0 ? 'text-rose-400' : 'text-white'}`}>{p.quantity}</TableCell>
                          <TableCell className="text-center text-slate-300 text-sm">
                            {p.target_stock_level
                              ? <span className={(p.quantity < p.target_stock_level ? 'text-amber-300 font-semibold' : 'text-slate-300')}>{p.target_stock_level}</span>
                              : <span className="text-slate-600">—</span>}
                          </TableCell>
                          <TableCell className="text-center text-xs">
                            {p.priority === 'Fast' && <Badge className="bg-rose-600 text-white">Fast</Badge>}
                            {p.priority === 'Medium' && <Badge className="bg-amber-500 text-white">Medium</Badge>}
                            {p.priority === 'Slow' && <Badge className="bg-slate-500 text-white">Slow</Badge>}
                            {!p.priority && <span className="text-slate-600">—</span>}
                          </TableCell>
                          <TableCell className="text-cyan-400 text-right">{formatCurrency((p.cost_price || 0) * (p.quantity || 0))}</TableCell>
                          <TableCell className="text-emerald-400 text-right font-semibold">{formatCurrency((p.selling_price || 0) * (p.quantity || 0))}</TableCell>
                          <TableCell className="text-slate-400 text-sm max-w-[120px] truncate">{p.vehicle_source || '-'}</TableCell>
                          <TableCell className="text-right">
                            <div className="flex gap-1 justify-end">
                              <Button size="sm" className="bg-blue-600 hover:bg-blue-700 text-white h-7 px-2" onClick={() => { setEditingProduct({...p}); setShowEditProduct(true); }}><Pencil className="h-3 w-3" /></Button>
                              <Button size="sm" className="bg-rose-600 hover:bg-rose-700 text-white h-7 px-2" onClick={() => handleDeleteProduct(p.id)}><Trash2 className="h-3 w-3" /></Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                      <TableRow className="border-slate-700 bg-slate-700/50">
                        <TableCell colSpan={7} className="text-white font-bold">TOTAL INVENTORY VALUE (at cost):</TableCell>
                        <TableCell className="text-cyan-400 text-right font-bold text-lg" data-testid="inventory-table-total-cost">{formatCurrency(inventoryValue)}</TableCell>
                        <TableCell colSpan={3} />
                      </TableRow>
                      <TableRow className="border-slate-700 bg-slate-700/30">
                        <TableCell colSpan={8} className="text-white font-bold">
                          TOTAL STOCK VALUE (qty on hand × selling price):
                        </TableCell>
                        <TableCell className="text-emerald-400 text-right font-bold text-lg" data-testid="inventory-table-total-retail">{formatCurrency(inventoryValueRetail)}</TableCell>
                        <TableCell colSpan={2} />
                      </TableRow>
                      <TableRow className="border-slate-700 bg-slate-800/40">
                        <TableCell colSpan={7} className="text-slate-300 font-semibold text-sm">Potential margin (sell − cost):</TableCell>
                        <TableCell className="text-amber-400 text-right font-semibold text-sm" data-testid="inventory-table-potential-margin">{formatCurrency(potentialMargin)}</TableCell>
                        <TableCell colSpan={3} />
                      </TableRow>
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* ========== BULK COST IMPORT TAB ========== */}
        <TabsContent value="cost-import">
          <CostPriceImportPanel />
        </TabsContent>

        {/* ========== BULK PURCHASE TAB ========== */}
        <TabsContent value="bulk-purchase">
          <div className="flex justify-center mb-6">
            <Dialog open={showBulkPurchase} onOpenChange={setShowBulkPurchase}>
              <DialogTrigger asChild>
                <Button className="bg-indigo-600 hover:bg-indigo-700 text-white px-8 py-3 text-base" data-testid="bulk-purchase-btn">
                  <Truck className="h-5 w-5 mr-2" /> New Bulk Purchase
                </Button>
              </DialogTrigger>
              <DialogContent className="bg-slate-800 border-slate-700 text-white max-w-2xl max-h-[85vh] overflow-y-auto">
                <DialogHeader><DialogTitle className="flex items-center gap-2"><Truck className="h-5 w-5" /> BULK PURCHASE (Buy Vehicle / Batch)</DialogTitle></DialogHeader>
                <p className="text-sm text-slate-400 mb-4">Buy a vehicle and split cost across parts. Existing stock gets weighted average cost.</p>
                <div className="space-y-4">
                  <div><Label>Description *</Label><Input value={bulkPurchase.description} onChange={(e) => setBulkPurchase({...bulkPurchase, description: e.target.value})} placeholder="e.g., Toyota Hilux 2018 Bakkie" className="bg-slate-700 border-slate-600" data-testid="bulk-description-input" /></div>
                  <div><Label>Total Cost (R) *</Label><Input type="number" step="0.01" value={bulkPurchase.total_cost} onChange={(e) => setBulkPurchase({...bulkPurchase, total_cost: e.target.value})} placeholder="e.g., 45000" className="bg-slate-700 border-slate-600" data-testid="bulk-total-cost-input" /></div>

                  <div className="border border-slate-600 rounded-lg p-3">
                    <div className="flex justify-between items-center mb-3">
                      <Label className="text-amber-400 font-semibold">Parts from this purchase:</Label>
                      <Button size="sm" onClick={addBulkItem} className="bg-emerald-600 hover:bg-emerald-700 h-7"><Plus className="h-3 w-3 mr-1" /> Add Part</Button>
                    </div>
                    {bulkPurchase.items.map((item, idx) => (
                      <div key={idx} className="grid grid-cols-12 gap-2 mb-2 items-end">
                        <div className="col-span-3">
                          {idx === 0 && <Label className="text-xs">Name</Label>}
                          <Input value={item.name} onChange={(e) => updateBulkItem(idx, 'name', e.target.value)} placeholder="Part name" className="bg-slate-700 border-slate-600 h-8 text-sm" />
                        </div>
                        <div className="col-span-3">
                          {idx === 0 && <Label className="text-xs">Category</Label>}
                          <Select value={item.category} onValueChange={(val) => updateBulkItem(idx, 'category', val)}>
                            <SelectTrigger className="bg-slate-700 border-slate-600 h-8 text-sm"><SelectValue placeholder="Cat..." /></SelectTrigger>
                            <SelectContent className="bg-slate-700 border-slate-600 max-h-60">{categories.map((c) => <SelectItem key={c.name} value={c.name}>{c.name}</SelectItem>)}</SelectContent>
                          </Select>
                        </div>
                        <div className="col-span-2">
                          {idx === 0 && <Label className="text-xs">Subcat</Label>}
                          {item.category && getSubcategories(item.category).length > 0 ? (
                            <Select value={item.subcategory} onValueChange={(val) => updateBulkItem(idx, 'subcategory', val)}>
                              <SelectTrigger className="bg-slate-700 border-slate-600 h-8 text-sm"><SelectValue placeholder="..." /></SelectTrigger>
                              <SelectContent className="bg-slate-700 border-slate-600 max-h-60">{getSubcategories(item.category).map((sc) => <SelectItem key={sc} value={sc}>{sc}</SelectItem>)}</SelectContent>
                            </Select>
                          ) : <Input value={item.subcategory || ''} onChange={(e) => updateBulkItem(idx, 'subcategory', e.target.value)} placeholder="Sub" className="bg-slate-700 border-slate-600 h-8 text-sm" />}
                        </div>
                        <div className="col-span-1">
                          {idx === 0 && <Label className="text-xs">Qty</Label>}
                          <Input type="number" min="1" value={item.quantity} onChange={(e) => updateBulkItem(idx, 'quantity', e.target.value)} className="bg-slate-700 border-slate-600 h-8 text-sm" />
                        </div>
                        <div className="col-span-2">
                          {idx === 0 && <Label className="text-xs">Cost Alloc (R)</Label>}
                          <Input type="number" step="0.01" value={item.cost_allocation} onChange={(e) => updateBulkItem(idx, 'cost_allocation', e.target.value)} placeholder="Auto" className="bg-slate-700 border-slate-600 h-8 text-sm" />
                        </div>
                        <div className="col-span-1">
                          {idx === 0 && <Label className="text-xs">&nbsp;</Label>}
                          <Button size="sm" variant="ghost" className="text-rose-400 hover:text-rose-300 h-8 w-8 p-0" onClick={() => removeBulkItem(idx)}><Trash2 className="h-3 w-3" /></Button>
                        </div>
                      </div>
                    ))}
                    <p className="text-xs text-slate-500 mt-2">Leave "Cost Alloc" empty = equal split of total cost across items.</p>
                  </div>

                  <Button onClick={handleBulkPurchase} className="w-full bg-indigo-600 hover:bg-indigo-700" data-testid="save-bulk-purchase-btn">RECORD PURCHASE & UPDATE INVENTORY</Button>
                </div>
              </DialogContent>
            </Dialog>
          </div>

          <Card className="bg-slate-800/50 border-slate-700">
            <CardHeader><CardTitle className="text-white flex items-center gap-2"><Truck className="h-5 w-5 text-indigo-400" /> PURCHASE HISTORY</CardTitle></CardHeader>
            <CardContent>
              {stockPurchases.length === 0 ? <p className="text-slate-400 text-center py-8">No bulk purchases yet.</p> : (
                <Table>
                  <TableHeader><TableRow className="border-slate-700">
                    <TableHead className="text-slate-400">Date</TableHead>
                    <TableHead className="text-slate-400">Description</TableHead>
                    <TableHead className="text-slate-400 text-right">Items</TableHead>
                    <TableHead className="text-slate-400 text-right">Total Cost</TableHead>
                  </TableRow></TableHeader>
                  <TableBody>
                    {stockPurchases.map((p) => (
                      <TableRow key={p.id} className="border-slate-700">
                        <TableCell className="text-slate-300">{p.date}</TableCell>
                        <TableCell className="text-white font-medium">{p.description}</TableCell>
                        <TableCell className="text-white text-right">{p.item_count}</TableCell>
                        <TableCell className="text-rose-400 text-right font-bold">{formatCurrency(p.total_cost)}</TableCell>
                      </TableRow>
                    ))}
                    <TableRow className="border-slate-700 bg-slate-700/50">
                      <TableCell colSpan={3} className="text-white font-bold">TOTAL INVESTED IN STOCK:</TableCell>
                      <TableCell className="text-rose-400 text-right font-bold text-lg">{formatCurrency(stockPurchases.reduce((s, p) => s + (p.total_cost || 0), 0))}</TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* ========== CAPITAL & SALARIES TAB ========== */}
        <TabsContent value="financials">
          <div className="grid md:grid-cols-2 gap-6">
            {/* Capital Injections */}
            <div>
              <div className="flex justify-center mb-4">
                <Dialog open={showAddCapital} onOpenChange={setShowAddCapital}>
                  <DialogTrigger asChild>
                    <Button className="bg-purple-600 hover:bg-purple-700 text-white px-6 py-2" data-testid="add-capital-btn">
                      <Plus className="h-4 w-4 mr-2" /> Add Capital Injection
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="bg-slate-800 border-slate-700 text-white">
                    <DialogHeader><DialogTitle className="flex items-center gap-2"><PiggyBank className="h-5 w-5" /> CAPITAL INJECTION</DialogTitle></DialogHeader>
                    <div className="space-y-4">
                      <div><Label>Description *</Label><Input value={newCapital.description} onChange={(e) => setNewCapital({...newCapital, description: e.target.value})} placeholder="e.g., Owner investment" className="bg-slate-700 border-slate-600" data-testid="capital-description-input" /></div>
                      <div className="grid grid-cols-2 gap-4">
                        <div><Label>Amount (R) *</Label><Input type="number" step="0.01" value={newCapital.amount} onChange={(e) => setNewCapital({...newCapital, amount: e.target.value})} placeholder="0.00" className="bg-slate-700 border-slate-600" data-testid="capital-amount-input" /></div>
                        <div><Label>Date</Label><Input type="date" value={newCapital.date} onChange={(e) => setNewCapital({...newCapital, date: e.target.value})} className="bg-slate-700 border-slate-600" /></div>
                      </div>
                      <div><Label>Source</Label><Input value={newCapital.source} onChange={(e) => setNewCapital({...newCapital, source: e.target.value})} placeholder="e.g., Owner, Loan" className="bg-slate-700 border-slate-600" /></div>
                      <Button onClick={handleAddCapital} className="w-full bg-purple-600 hover:bg-purple-700" data-testid="save-capital-btn">SAVE</Button>
                    </div>
                  </DialogContent>
                </Dialog>
              </div>
              <Card className="bg-slate-800/50 border-slate-700">
                <CardHeader><CardTitle className="text-white flex items-center gap-2"><PiggyBank className="h-5 w-5 text-purple-400" /> CAPITAL INJECTIONS</CardTitle></CardHeader>
                <CardContent>
                  {capitalInjections.length === 0 ? <p className="text-slate-400 text-center py-4">None recorded.</p> : (
                    <Table>
                      <TableHeader><TableRow className="border-slate-700">
                        <TableHead className="text-slate-400">Date</TableHead><TableHead className="text-slate-400">Description</TableHead>
                        <TableHead className="text-slate-400 text-right">Amount</TableHead><TableHead className="text-slate-400 text-right"></TableHead>
                      </TableRow></TableHeader>
                      <TableBody>
                        {capitalInjections.map((c) => (
                          <TableRow key={c.id} className="border-slate-700">
                            <TableCell className="text-slate-300">{c.date}</TableCell>
                            <TableCell className="text-white">
                              {c.description}{c.source ? ` (${c.source})` : ''}
                              {c.purpose && (
                                <span className={`ml-2 px-1.5 py-0.5 rounded text-[9px] font-bold ${c.purpose === 'bakkie' ? 'bg-orange-700/70 text-white' : 'bg-blue-700/70 text-white'}`} data-testid={`purpose-${c.id}`}>
                                  {c.purpose === 'bakkie' ? 'BAKKIE' : c.purpose === 'stock' ? 'STOCK' : c.purpose.toUpperCase()}
                                </span>
                              )}
                              {c.auto_generated && (
                                <span className="ml-2 px-1.5 py-0.5 rounded text-[9px] font-bold bg-emerald-700/70 text-white" title={`Auto-routed from received PO ${c.po_number || ''}`}>
                                  AUTO · PO
                                </span>
                              )}
                            </TableCell>
                            <TableCell className="text-purple-400 text-right font-bold">{formatCurrency(c.amount)}</TableCell>
                            <TableCell className="text-right">
                              {c.auto_generated ? (
                                <span className="text-[10px] text-slate-500 italic">cancel PO to remove</span>
                              ) : (
                                <Button size="sm" variant="ghost" className="text-rose-400 h-7 w-7 p-0" onClick={() => handleDeleteCapital(c.id)}><Trash2 className="h-3 w-3" /></Button>
                              )}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  )}
                </CardContent>
              </Card>
            </div>

            {/* Salaries */}
            <div>
              <div className="flex justify-center mb-4">
                <Dialog open={showAddSalary} onOpenChange={setShowAddSalary}>
                  <DialogTrigger asChild>
                    <Button className="bg-amber-600 hover:bg-amber-700 text-white px-6 py-2" data-testid="add-salary-btn">
                      <Plus className="h-4 w-4 mr-2" /> Add Salary Payment
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="bg-slate-800 border-slate-700 text-white">
                    <DialogHeader><DialogTitle className="flex items-center gap-2"><Banknote className="h-5 w-5" /> SALARY PAYMENT</DialogTitle></DialogHeader>
                    <div className="space-y-4">
                      <div><Label>Employee Name *</Label><Input value={newSalary.employee_name} onChange={(e) => setNewSalary({...newSalary, employee_name: e.target.value})} placeholder="e.g., Leeton" className="bg-slate-700 border-slate-600" data-testid="salary-employee-input" /></div>
                      <div className="grid grid-cols-2 gap-4">
                        <div><Label>Amount (R) *</Label><Input type="number" step="0.01" value={newSalary.amount} onChange={(e) => setNewSalary({...newSalary, amount: e.target.value})} placeholder="0.00" className="bg-slate-700 border-slate-600" data-testid="salary-amount-input" /></div>
                        <div><Label>Date</Label><Input type="date" value={newSalary.date} onChange={(e) => setNewSalary({...newSalary, date: e.target.value})} className="bg-slate-700 border-slate-600" /></div>
                      </div>
                      <div><Label>Period</Label><Input value={newSalary.period} onChange={(e) => setNewSalary({...newSalary, period: e.target.value})} placeholder="e.g., February 2026" className="bg-slate-700 border-slate-600" /></div>
                      <Button onClick={handleAddSalary} className="w-full bg-amber-600 hover:bg-amber-700" data-testid="save-salary-btn">SAVE</Button>
                    </div>
                  </DialogContent>
                </Dialog>
              </div>
              <Card className="bg-slate-800/50 border-slate-700">
                <CardHeader><CardTitle className="text-white flex items-center gap-2"><Banknote className="h-5 w-5 text-amber-400" /> SALARY PAYMENTS</CardTitle></CardHeader>
                <CardContent>
                  {salaries.length === 0 ? <p className="text-slate-400 text-center py-4">None recorded.</p> : (
                    <Table>
                      <TableHeader><TableRow className="border-slate-700">
                        <TableHead className="text-slate-400">Date</TableHead><TableHead className="text-slate-400">Employee</TableHead>
                        <TableHead className="text-slate-400">Period</TableHead><TableHead className="text-slate-400 text-right">Amount</TableHead>
                        <TableHead className="text-slate-400 text-right"></TableHead>
                      </TableRow></TableHeader>
                      <TableBody>
                        {salaries.map((s) => (
                          <TableRow key={s.id} className="border-slate-700">
                            <TableCell className="text-slate-300">{s.date}</TableCell>
                            <TableCell className="text-white">{s.employee_name}</TableCell>
                            <TableCell className="text-slate-400">{s.period || '-'}</TableCell>
                            <TableCell className="text-amber-400 text-right font-bold">{formatCurrency(s.amount)}</TableCell>
                            <TableCell className="text-right"><Button size="sm" variant="ghost" className="text-rose-400 h-7 w-7 p-0" onClick={() => handleDeleteSalary(s.id)}><Trash2 className="h-3 w-3" /></Button></TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  )}
                </CardContent>
              </Card>
            </div>
          </div>
        </TabsContent>

        {/* ========== TRUE PROFIT TAB ========== */}
        <TabsContent value="true-profit">
          {trueProfit ? (
            <div className="space-y-6">
              <Card className="bg-slate-800/50 border-slate-700">
                <CardHeader><CardTitle className="text-white flex items-center gap-2"><BarChart3 className="h-5 w-5 text-teal-400" /> PROFIT & LOSS - {new Date().toLocaleString('default', { month: 'long', year: 'numeric' })}</CardTitle></CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {/* Revenue */}
                    <div className="flex justify-between items-center py-2 border-b border-slate-700">
                      <span className="text-slate-300 text-lg">Revenue (Sales)</span>
                      <span className="text-emerald-400 font-bold text-xl" data-testid="tp-revenue">{formatCurrency(trueProfit.revenue)}</span>
                    </div>

                    {/* Expenses */}
                    <div className="flex justify-between items-center py-2 border-b border-slate-700">
                      <span className="text-slate-300">- Expenses</span>
                      <span className="text-rose-400 font-bold" data-testid="tp-expenses">{formatCurrency(trueProfit.expenses)}</span>
                    </div>

                    {/* Cash on Hand */}
                    <div className={`flex justify-between items-center py-3 px-3 rounded ${trueProfit.cash_on_hand >= 0 ? 'bg-blue-900/30 border border-blue-600' : 'bg-red-900/30 border border-red-600'}`}>
                      <span className="text-white text-lg font-bold">= CASH ON HAND (Sales - Expenses)</span>
                      <span className={`font-black text-2xl ${trueProfit.cash_on_hand >= 0 ? 'text-blue-400' : 'text-red-400'}`} data-testid="tp-cash-on-hand">{formatCurrency(trueProfit.cash_on_hand)}</span>
                    </div>

                    <div className="h-2" />

                    {/* Salaries */}
                    <div className="flex justify-between items-center py-2 border-b border-slate-700">
                      <span className="text-slate-300">- Basic Salaries Paid</span>
                      <span className="text-rose-400 font-bold" data-testid="tp-salaries">{formatCurrency(trueProfit.salaries)}</span>
                    </div>
                    <div className="flex justify-between items-center py-2 border-b border-slate-700">
                      <span className="text-slate-300">- Commissions Earned</span>
                      <span className="text-rose-400 font-bold" data-testid="tp-commissions">{formatCurrency(trueProfit.commissions)}</span>
                    </div>
                    <div className="flex justify-between items-center py-2 border-b border-slate-700 bg-slate-700/20 px-3 rounded">
                      <span className="text-white font-semibold">Total Salary Cost (Basic + Commission)</span>
                      <span className="text-amber-400 font-bold">{formatCurrency(trueProfit.total_salary_cost)}</span>
                    </div>

                    {/* Operating Profit */}
                    <div className={`flex justify-between items-center py-4 px-4 rounded-lg mt-2 ${trueProfit.operating_profit >= 0 ? 'bg-teal-900/50 border-2 border-teal-500' : 'bg-red-900/50 border-2 border-red-500'}`}>
                      <div>
                        <span className="text-white text-xl font-black block">= OPERATING PROFIT</span>
                        <span className="text-slate-400 text-xs">Sales - Expenses - Salaries - Commission</span>
                      </div>
                      <span className={`font-black text-3xl ${trueProfit.operating_profit >= 0 ? 'text-teal-400' : 'text-red-400'}`} data-testid="tp-operating-profit">{formatCurrency(trueProfit.operating_profit)}</span>
                    </div>

                    <div className="h-2" />

                    {/* Capital & COGS info */}
                    <div className="flex justify-between items-center py-2 border-b border-slate-700">
                      <span className="text-slate-300">COGS (Cost of Goods Sold)</span>
                      <span className="text-slate-400 font-bold" data-testid="tp-cogs">{formatCurrency(trueProfit.cogs)}</span>
                    </div>
                    <div className="flex justify-between items-center py-2 border-b border-slate-700">
                      <span className="text-slate-300">Capital Injections (Money In)</span>
                      <span className="text-purple-400 font-bold" data-testid="tp-capital">{formatCurrency(trueProfit.capital_injections)}</span>
                    </div>
                    <div className="flex justify-between items-center py-2 border-b border-slate-700">
                      <span className="text-slate-300">Debt Repayments (offset)</span>
                      <span className="text-red-400 font-bold" data-testid="tp-debt">-{formatCurrency(trueProfit.debt_repayments || 0)}</span>
                    </div>
                    <div className="flex justify-between items-center py-2 border-b border-slate-700">
                      <span className="text-white font-semibold">Net Capital In</span>
                      <span className="text-emerald-400 font-bold text-lg" data-testid="tp-net-capital">{formatCurrency(trueProfit.net_capital_injections || 0)}</span>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <div className="grid md:grid-cols-2 gap-6">
                <Card className="bg-slate-800/50 border-slate-700">
                  <CardContent className="p-4">
                    <div className="flex items-center gap-2 mb-2"><Warehouse className="h-5 w-5 text-cyan-400" /><span className="text-white font-semibold">Inventory Summary</span></div>
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between"><span className="text-slate-400">Total Stock Items:</span><span className="text-white font-bold">{trueProfit.total_stock_items}</span></div>
                      <div className="flex justify-between"><span className="text-slate-400">Inventory Value (at cost):</span><span className="text-cyan-400 font-bold">{formatCurrency(trueProfit.inventory_value_at_cost ?? trueProfit.inventory_value)}</span></div>
                    </div>
                  </CardContent>
                </Card>
                <Card className="bg-slate-800/50 border-slate-700">
                  <CardContent className="p-4">
                    <div className="flex items-center gap-2 mb-2"><TrendingUp className="h-5 w-5 text-emerald-400" /><span className="text-white font-semibold">Profitability</span></div>
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between"><span className="text-slate-400">Gross Margin:</span><span className="text-white font-bold">{trueProfit.revenue > 0 ? ((trueProfit.gross_profit / trueProfit.revenue) * 100).toFixed(1) : 0}%</span></div>
                      <div className="flex justify-between"><span className="text-slate-400">Operating Margin:</span><span className={`font-bold ${trueProfit.operating_profit >= 0 ? 'text-teal-400' : 'text-red-400'}`}>{trueProfit.revenue > 0 ? ((trueProfit.operating_profit / trueProfit.revenue) * 100).toFixed(1) : 0}%</span></div>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </div>
          ) : <p className="text-slate-400 text-center py-8">Loading profit data...</p>}
        </TabsContent>

        {/* ========== P&L STATEMENT TAB ========== */}
        <TabsContent value="pnl-statement">
          <SparesPnLPanel />
        </TabsContent>

        {/* ========== DEBT REPAYMENT TAB ========== */}
        <TabsContent value="debt">
          <Card className="bg-slate-800/50 border-slate-700">
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle className="text-white flex items-center gap-2">
                  <TrendingDown className="h-5 w-5 text-red-400" /> DEBT REPAYMENT
                </CardTitle>
                <p className="text-xs text-slate-400 mt-1">Repayments are deducted from capital injections in the True Profit view.</p>
              </div>
              <Button onClick={() => setShowAddDebt(true)} className="bg-red-600 hover:bg-red-700" data-testid="add-debt-btn">
                <Plus className="h-4 w-4 mr-2" /> ADD REPAYMENT
              </Button>
            </CardHeader>
            <CardContent>
              {/* Summary strip */}
              <div className="grid grid-cols-3 gap-3 mb-4">
                <Card className="bg-purple-900/30 border-purple-700"><CardContent className="p-3">
                  <div className="text-[10px] uppercase text-purple-300 tracking-wider">Capital Injected (month)</div>
                  <p className="text-lg font-bold text-purple-300">{formatCurrency(trueProfit?.capital_injections || 0)}</p>
                </CardContent></Card>
                <Card className="bg-red-900/30 border-red-700"><CardContent className="p-3">
                  <div className="text-[10px] uppercase text-red-300 tracking-wider">Debt Repaid (month)</div>
                  <p className="text-lg font-bold text-red-300">-{formatCurrency(trueProfit?.debt_repayments || 0)}</p>
                </CardContent></Card>
                <Card className="bg-emerald-900/30 border-emerald-700"><CardContent className="p-3">
                  <div className="text-[10px] uppercase text-emerald-300 tracking-wider">Net Capital In</div>
                  <p className="text-lg font-bold text-emerald-300">{formatCurrency(trueProfit?.net_capital_injections || 0)}</p>
                </CardContent></Card>
              </div>

              {debtRepayments.length === 0 ? (
                <p className="text-slate-400 text-center py-8">No debt repayments yet. Click ADD REPAYMENT to record one.</p>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow className="border-slate-700">
                      <TableHead className="text-slate-400">Date</TableHead>
                      <TableHead className="text-slate-400">Description</TableHead>
                      <TableHead className="text-slate-400">Paid To</TableHead>
                      <TableHead className="text-slate-400 text-right">Amount</TableHead>
                      <TableHead className="text-slate-400 text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {debtRepayments.map(d => (
                      <TableRow key={d.id} className="border-slate-700 hover:bg-slate-700/30" data-testid={`debt-row-${d.id}`}>
                        <TableCell className="text-slate-300 font-mono text-xs">{d.date}</TableCell>
                        <TableCell className="text-white">{d.description}</TableCell>
                        <TableCell className="text-slate-300">{d.paid_to || '-'}</TableCell>
                        <TableCell className="text-right text-red-400 font-semibold">-{formatCurrency(d.amount)}</TableCell>
                        <TableCell className="text-right">
                          <Button size="sm" variant="ghost" className="text-rose-400 h-7 w-7 p-0" onClick={() => handleDeleteDebt(d.id)}>
                            <Trash2 className="h-3 w-3" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                    <TableRow className="border-slate-700 bg-slate-700/40 font-bold">
                      <TableCell colSpan={3} className="text-white">TOTAL</TableCell>
                      <TableCell className="text-right text-red-300">-{formatCurrency(debtRepayments.reduce((a, b) => a + (b.amount || 0), 0))}</TableCell>
                      <TableCell></TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>

          {/* Add debt dialog */}
          <Dialog open={showAddDebt} onOpenChange={setShowAddDebt}>
            <DialogContent className="bg-slate-800 border-slate-700 text-white">
              <DialogHeader><DialogTitle className="flex items-center gap-2"><TrendingDown className="h-5 w-5 text-red-400" /> RECORD DEBT REPAYMENT</DialogTitle></DialogHeader>
              <div className="space-y-3">
                <div><Label>Date</Label><Input type="date" value={newDebt.date} onChange={(e) => setNewDebt({ ...newDebt, date: e.target.value })} className="bg-slate-700 border-slate-600" /></div>
                <div><Label>Description</Label><Input value={newDebt.description} onChange={(e) => setNewDebt({ ...newDebt, description: e.target.value })} placeholder="e.g. Loan repayment instalment" className="bg-slate-700 border-slate-600" data-testid="debt-desc" /></div>
                <div><Label>Paid To</Label><Input value={newDebt.paid_to} onChange={(e) => setNewDebt({ ...newDebt, paid_to: e.target.value })} placeholder="e.g. J. Smith (owner)" className="bg-slate-700 border-slate-600" /></div>
                <div><Label>Amount (R)</Label><Input type="number" min="0" step="0.01" value={newDebt.amount} onChange={(e) => setNewDebt({ ...newDebt, amount: e.target.value })} className="bg-slate-700 border-slate-600" data-testid="debt-amount" /></div>
                <Button onClick={handleAddDebt} className="w-full bg-red-600 hover:bg-red-700" data-testid="save-debt-btn">SAVE REPAYMENT</Button>
              </div>
            </DialogContent>
          </Dialog>
        </TabsContent>

        {/* ========== SALESMEN TAB ========== */}
        <TabsContent value="salesmen">
          <Card className="bg-slate-800/50 border-slate-700">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="text-white flex items-center gap-2"><Users className="h-5 w-5 text-purple-400" /> MANAGE SALESMEN</CardTitle>
              <div className="flex gap-2">
                <Button onClick={handleIssueAllPayslips} className="bg-amber-600 hover:bg-amber-700" data-testid="issue-all-payslips-btn">
                  <Receipt className="h-4 w-4 mr-2" /> Issue All Payslips
                </Button>
                <Dialog open={showAddSalesman} onOpenChange={setShowAddSalesman}>
                <DialogTrigger asChild><Button className="bg-emerald-600 hover:bg-emerald-700" data-testid="add-salesman-btn"><Plus className="h-4 w-4 mr-2" /> Add Salesman</Button></DialogTrigger>
                <DialogContent className="bg-slate-800 border-slate-700 text-white">
                  <DialogHeader><DialogTitle>Add New Salesman</DialogTitle></DialogHeader>
                  <div className="space-y-4">
                    <div><Label>Full Name *</Label><Input value={newSalesman.full_name} onChange={(e) => setNewSalesman({...newSalesman, full_name: e.target.value})} className="bg-slate-700 border-slate-600" data-testid="new-salesman-name" /></div>
                    <div><Label>Email *</Label><Input type="email" value={newSalesman.email} onChange={(e) => setNewSalesman({...newSalesman, email: e.target.value})} className="bg-slate-700 border-slate-600" data-testid="new-salesman-email" /></div>
                    <div><Label>Password *</Label><Input type="password" value={newSalesman.password} onChange={(e) => setNewSalesman({...newSalesman, password: e.target.value})} className="bg-slate-700 border-slate-600" data-testid="new-salesman-password" /></div>
                    <div className="grid grid-cols-2 gap-4">
                      <div><Label>Commission Rate (%)</Label><Input type="number" value={newSalesman.commission_rate} onChange={(e) => setNewSalesman({...newSalesman, commission_rate: parseFloat(e.target.value)})} className="bg-slate-700 border-slate-600" /></div>
                      <div><Label>Basic Salary (R)</Label><Input type="number" value={newSalesman.basic_salary} onChange={(e) => setNewSalesman({...newSalesman, basic_salary: parseFloat(e.target.value)})} className="bg-slate-700 border-slate-600" /></div>
                    </div>
                    <Button onClick={handleAddSalesman} className="w-full bg-emerald-600 hover:bg-emerald-700" data-testid="save-salesman-btn">Add Salesman</Button>
                  </div>
                </DialogContent>
              </Dialog>
              </div>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader><TableRow className="border-slate-700">
                  <TableHead className="text-slate-400">Name</TableHead><TableHead className="text-slate-400">Email</TableHead>
                  <TableHead className="text-slate-400 text-right">Commission %</TableHead><TableHead className="text-slate-400 text-right">Basic Salary</TableHead>
                  <TableHead className="text-slate-400 text-right">Loan</TableHead>
                  <TableHead className="text-slate-400 text-right">Last Payslip</TableHead>
                  <TableHead className="text-slate-400 text-right">Actions</TableHead>
                </TableRow></TableHeader>
                <TableBody>
                  {salesmen.map((salesman) => (
                    <TableRow key={salesman.id} className="border-slate-700">
                      <TableCell className="text-white font-medium">{salesman.full_name}</TableCell>
                      <TableCell className="text-slate-400">{salesman.email}</TableCell>
                      <TableCell className="text-amber-400 text-right">{salesman.commission_rate}%</TableCell>
                      <TableCell className="text-emerald-400 text-right">{formatCurrency(salesman.basic_salary)}</TableCell>
                      <TableCell className="text-right">
                        {(salesman.loan_balance || 0) > 0 ? (
                          <span className="text-rose-400 font-semibold">{formatCurrency(salesman.loan_balance)}</span>
                        ) : (
                          <span className="text-slate-600 text-xs">—</span>
                        )}
                      </TableCell>
                      <TableCell className="text-right text-slate-400 text-xs">
                        {salesman.last_payslip_period ? periodLabel(salesman.last_payslip_period) : <span className="text-slate-600">—</span>}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex gap-1 justify-end">
                          <Button size="sm" className="bg-amber-600 hover:bg-amber-700 text-white h-7 px-2" onClick={() => handleIssuePayslip(salesman)} title="Issue payslip — auto-deduct loan + reset incentive" data-testid={`issue-payslip-${salesman.id}`}>
                            <Receipt className="h-3 w-3 mr-1" />Issue
                          </Button>
                          <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 text-white h-7 px-2" onClick={() => handleDownloadPayslip(salesman)} title="Download payslip PDF for any month" data-testid={`download-payslip-${salesman.id}`}>
                            <FileDown className="h-3 w-3 mr-1" />PDF
                          </Button>
                          <Button size="sm" className="bg-blue-600 hover:bg-blue-700 text-white h-7 px-2" onClick={() => { setSelectedSalesman({...salesman}); setShowEditSalesman(true); }}><Pencil className="h-3 w-3 mr-1" />Edit</Button>
                          <Button size="sm" className="bg-rose-600 hover:bg-rose-700 text-white h-7 px-2" onClick={() => handleDeleteSalesman(salesman.id, salesman.full_name)}><Trash2 className="h-3 w-3 mr-1" />Delete</Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* ========== PARTS CATALOG TAB ========== */}
        <TabsContent value="parts-catalog">
          <PartsCatalogPanel />
        </TabsContent>

        {/* ========== SALES HISTORY TAB ========== */}
        <TabsContent value="sales-history">
          <Card className="bg-slate-800/50 border-slate-700">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                <History className="h-5 w-5 text-fuchsia-400" /> SALES HISTORY SEARCH
              </CardTitle>
              <p className="text-slate-400 text-sm">Search past sales by description, customer, salesman or category. Filter by date range.</p>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-[1fr_180px_180px_auto_auto] gap-3 mb-5">
                <div>
                  <Label className="text-slate-300 text-xs">Search</Label>
                  <div className="relative">
                    <Search className="h-4 w-4 absolute left-2.5 top-3 text-slate-400" />
                    <Input
                      value={salesSearchQuery}
                      onChange={(e) => setSalesSearchQuery(e.target.value)}
                      onKeyDown={(e) => { if (e.key === 'Enter') runSalesSearch(); }}
                      placeholder="Description, customer, salesman, category…"
                      className="bg-slate-700 border-slate-600 pl-8"
                      data-testid="sales-history-search-input"
                    />
                  </div>
                </div>
                <div>
                  <Label className="text-slate-300 text-xs">From Date</Label>
                  <Input
                    type="date"
                    value={salesSearchStart}
                    onChange={(e) => setSalesSearchStart(e.target.value)}
                    className="bg-slate-700 border-slate-600"
                    data-testid="sales-history-start-date"
                  />
                </div>
                <div>
                  <Label className="text-slate-300 text-xs">To Date</Label>
                  <Input
                    type="date"
                    value={salesSearchEnd}
                    onChange={(e) => setSalesSearchEnd(e.target.value)}
                    className="bg-slate-700 border-slate-600"
                    data-testid="sales-history-end-date"
                  />
                </div>
                <div className="flex items-end">
                  <Button
                    onClick={runSalesSearch}
                    disabled={salesSearchLoading}
                    className="bg-fuchsia-600 hover:bg-fuchsia-700 text-white w-full md:w-auto"
                    data-testid="sales-history-search-btn"
                  >
                    <Search className="h-4 w-4 mr-1" /> {salesSearchLoading ? 'Searching…' : 'Search'}
                  </Button>
                </div>
                <div className="flex items-end">
                  <Button
                    onClick={clearSalesSearch}
                    variant="outline"
                    className="border-slate-600 text-slate-200 hover:bg-slate-700 w-full md:w-auto"
                    data-testid="sales-history-clear-btn"
                  >
                    <X className="h-4 w-4 mr-1" /> Clear
                  </Button>
                </div>
              </div>

              {salesSearchRan && (
                <div className="bg-slate-700/50 p-3 rounded-lg mb-4 flex items-center justify-between" data-testid="sales-history-summary">
                  <p className="text-slate-300 text-sm">
                    <span className="font-bold text-white">{salesSearchCount}</span> result{salesSearchCount === 1 ? '' : 's'} shown
                    {salesSearchTruncated && (
                      <span className="ml-1 text-amber-300 text-xs">
                        · of <b>{salesSearchTotalCount}</b> total (list capped at 5000)
                      </span>
                    )}
                  </p>
                  <p className="text-emerald-400 font-bold text-lg" data-testid="sales-history-total">
                    Total: {formatCurrency(salesSearchTotal)}
                  </p>
                </div>
              )}

              {salesSearchRan && salesSearchResults.length === 0 ? (
                <p className="text-slate-400 text-center py-8" data-testid="sales-history-empty">No matching sales found. Try a different search term or date range.</p>
              ) : salesSearchResults.length > 0 ? (
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow className="border-slate-700">
                        <TableHead className="text-slate-400">Date</TableHead>
                        <TableHead className="text-slate-400">Customer</TableHead>
                        <TableHead className="text-slate-400">Part / Description</TableHead>
                        <TableHead className="text-slate-400">Salesman</TableHead>
                        <TableHead className="text-slate-400 text-right">Qty</TableHead>
                        <TableHead className="text-slate-400 text-right">Price</TableHead>
                        <TableHead className="text-slate-400 text-right">Total</TableHead>
                        <TableHead className="text-slate-400 text-center">Method</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody data-testid="sales-history-results">
                      {salesSearchResults.map((sale) => {
                        const pm = (sale.payment_method || 'cash').toLowerCase();
                        const pmStyles = { cash: 'bg-emerald-600', online: 'bg-blue-600', eft: 'bg-purple-600', card: 'bg-amber-600', credit: 'bg-rose-600' };
                        return (
                          <TableRow key={sale.id} className="border-slate-700" data-testid={`sales-history-row-${sale.id}`}>
                            <TableCell className="text-slate-300 text-sm whitespace-nowrap">{sale.date}</TableCell>
                            <TableCell className="text-cyan-400">{sale.customer_name || '-'}</TableCell>
                            <TableCell className="text-white">{sale.part_description}</TableCell>
                            <TableCell className="text-amber-400 text-sm">{sale.salesman_name}</TableCell>
                            <TableCell className="text-white text-right">{sale.quantity}</TableCell>
                            <TableCell className="text-white text-right">{formatCurrency(sale.unit_price)}</TableCell>
                            <TableCell className="text-emerald-400 text-right font-bold">{formatCurrency(sale.total)}</TableCell>
                            <TableCell className="text-center">
                              <span className={`px-2 py-0.5 rounded text-xs font-semibold text-white ${pmStyles[pm] || 'bg-slate-700'}`}>{pm.toUpperCase()}</span>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </div>
              ) : !salesSearchRan && (
                <p className="text-slate-500 text-center py-8 text-sm">
                  Enter a search term and/or date range above, then click <strong>Search</strong>.
                </p>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* ========== STOCK TAKE TAB ========== */}
        <TabsContent value="stock-take">
          <StockTakePanel />
        </TabsContent>

        <TabsContent value="vehicles-cogs">
          <VehiclesCostingPanel />
        </TabsContent>

        {/* ========== INVOICES TAB ========== */}
        <TabsContent value="invoices">
          <InvoicesPanel />
        </TabsContent>

        {/* ========== PROCUREMENT TAB ========== */}
        <TabsContent value="procurement">
          <ProcurementContent />
        </TabsContent>

        {/* ========== EXPANSION BUDGET TAB ========== */}
        <TabsContent value="expansion-budget">
          <ExpansionBudgetPanel />
        </TabsContent>

        {/* ========== INVESTMENT vs SALES TAB ========== */}
        <TabsContent value="investment">
          <Card className="bg-slate-800 border-slate-700">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                <BarChart3 className="h-5 w-5 text-yellow-400" /> Capital Injected vs Sales — Last 12 Months
              </CardTitle>
              <p className="text-xs text-slate-400 mt-1">Are we investing more than we&apos;re selling? Higher capital than sales means the business is being subsidised for that month.</p>
            </CardHeader>
            <CardContent>
              {!investmentData ? (
                <div className="text-slate-400 text-sm py-8 text-center">Loading investment data…</div>
              ) : (
                <div className="space-y-6">
                  {/* Totals + forecast header */}
                  <div className="grid grid-cols-1 md:grid-cols-4 gap-3" data-testid="investment-summary">
                    <div className="bg-emerald-900/40 border border-emerald-700 rounded-lg p-3">
                      <div className="text-xs text-emerald-300 uppercase font-semibold">Sales (12mo)</div>
                      <div className="text-xl font-bold text-white" data-testid="ivs-total-sales">{formatCurrency(investmentData.totals?.sales)}</div>
                    </div>
                    <div className="bg-amber-900/40 border border-amber-700 rounded-lg p-3">
                      <div className="text-xs text-amber-300 uppercase font-semibold">Capital Injected (12mo)</div>
                      <div className="text-xl font-bold text-white" data-testid="ivs-total-capital">{formatCurrency(investmentData.totals?.capital_injected)}</div>
                    </div>
                    <div className={`${investmentData.totals?.over_invested ? 'bg-rose-900/40 border-rose-700' : 'bg-cyan-900/40 border-cyan-700'} border rounded-lg p-3`}>
                      <div className={`text-xs uppercase font-semibold ${investmentData.totals?.over_invested ? 'text-rose-300' : 'text-cyan-300'}`}>Net (Sales − Capital)</div>
                      <div className="text-xl font-bold text-white flex items-center gap-2" data-testid="ivs-net">
                        {formatCurrency(investmentData.totals?.net)}
                        {investmentData.totals?.over_invested ? (
                          <AlertTriangle className="h-4 w-4 text-rose-400" title="Over-invested" />
                        ) : (
                          <CheckCircle2 className="h-4 w-4 text-emerald-400" title="Recovered" />
                        )}
                      </div>
                    </div>
                    <div className="bg-violet-900/40 border border-violet-700 rounded-lg p-3" data-testid="ivs-forecast">
                      <div className="text-xs text-violet-300 uppercase font-semibold flex items-center gap-1">
                        <LineChartIcon className="h-3 w-3" /> Sales Forecast · {investmentData.forecast?.next_month || '—'}
                      </div>
                      <div className="text-xl font-bold text-white" data-testid="ivs-forecast-value">{formatCurrency(investmentData.forecast?.predicted_sales)}</div>
                      <div className="text-[10px] text-violet-200/80 mt-0.5">
                        {investmentData.forecast?.method || '—'}
                        {investmentData.forecast?.momentum_pct != null && (
                          <span className={`ml-2 font-semibold ${investmentData.forecast.momentum_pct >= 0 ? 'text-emerald-300' : 'text-rose-300'}`} data-testid="ivs-momentum">
                            {investmentData.forecast.momentum_pct >= 0 ? '▲' : '▼'} {Math.abs(investmentData.forecast.momentum_pct)}% MoM
                          </span>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Bar+Line chart */}
                  <div className="bg-slate-900/60 border border-slate-700 rounded-lg p-3" data-testid="investment-chart">
                    <ResponsiveContainer width="100%" height={320}>
                      <ComposedChart data={(investmentData.months || []).concat(investmentData.forecast ? [{
                        month: investmentData.forecast.next_month,
                        sales: 0,
                        forecast_sales: investmentData.forecast.predicted_sales,
                        capital_injected: 0,
                      }] : [])}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                        <XAxis dataKey="month" stroke="#94a3b8" fontSize={11} />
                        <YAxis stroke="#94a3b8" fontSize={11} tickFormatter={(v) => `R${(v/1000).toFixed(0)}k`} />
                        <Tooltip
                          contentStyle={{ backgroundColor: '#1e293b', border: '1px solid #475569', color: '#fff' }}
                          formatter={(v) => formatCurrency(v)}
                        />
                        <Legend wrapperStyle={{ color: '#cbd5e1', fontSize: 12 }} />
                        <Bar dataKey="sales" name="Sales" fill="#10b981" radius={[4,4,0,0]} />
                        <Bar dataKey="capital_injected" name="Capital Injected" fill="#f59e0b" radius={[4,4,0,0]} />
                        <Line
                          type="monotone"
                          dataKey="forecast_sales"
                          name="Forecast"
                          stroke="#a78bfa"
                          strokeWidth={3}
                          strokeDasharray="6 4"
                          dot={{ r: 6, fill: '#a78bfa' }}
                          connectNulls
                        />
                        <ReferenceLine y={0} stroke="#64748b" />
                      </ComposedChart>
                    </ResponsiveContainer>
                  </div>

                  {/* Monthly table */}
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow className="border-slate-700 hover:bg-transparent">
                          <TableHead className="text-slate-400">Month</TableHead>
                          <TableHead className="text-slate-400 text-right">Sales</TableHead>
                          <TableHead className="text-slate-400 text-right"># Sales</TableHead>
                          <TableHead className="text-slate-400 text-right">Capital Injected</TableHead>
                          <TableHead className="text-slate-400 text-right">Net</TableHead>
                          <TableHead className="text-slate-400 text-center">Status</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {(investmentData.months || []).map((r) => (
                          <TableRow key={r.month} className="border-slate-700/50" data-testid={`ivs-row-${r.month}`}>
                            <TableCell className="text-white font-mono text-sm">{r.month}</TableCell>
                            <TableCell className="text-right text-emerald-300 font-mono">{formatCurrency(r.sales)}</TableCell>
                            <TableCell className="text-right text-slate-400 text-sm">{r.sales_count}</TableCell>
                            <TableCell className="text-right text-amber-300 font-mono">{formatCurrency(r.capital_injected)}</TableCell>
                            <TableCell className={`text-right font-mono font-semibold ${r.net < 0 ? 'text-rose-400' : 'text-cyan-300'}`}>{formatCurrency(r.net)}</TableCell>
                            <TableCell className="text-center">
                              {r.sales_count === 0 && r.capital_injected === 0 ? (
                                <span className="text-slate-600 text-xs">—</span>
                              ) : r.over_invested ? (
                                <span className="inline-flex items-center gap-1 text-rose-400 text-xs font-semibold" data-testid={`ivs-flag-${r.month}`}><AlertTriangle className="h-3 w-3" /> Over-invested</span>
                              ) : (
                                <span className="inline-flex items-center gap-1 text-emerald-400 text-xs font-semibold"><CheckCircle2 className="h-3 w-3" /> OK</span>
                              )}
                            </TableCell>
                          </TableRow>
                        ))}
                        <TableRow className="border-slate-700 bg-slate-700/40 font-bold">
                          <TableCell className="text-white">TOTAL</TableCell>
                          <TableCell className="text-right text-emerald-300 font-mono">{formatCurrency(investmentData.totals?.sales)}</TableCell>
                          <TableCell />
                          <TableCell className="text-right text-amber-300 font-mono">{formatCurrency(investmentData.totals?.capital_injected)}</TableCell>
                          <TableCell className={`text-right font-mono ${(investmentData.totals?.net || 0) < 0 ? 'text-rose-400' : 'text-cyan-300'}`}>{formatCurrency(investmentData.totals?.net)}</TableCell>
                          <TableCell className="text-center">
                            {investmentData.totals?.over_invested ? (
                              <span className="inline-flex items-center gap-1 text-rose-400 text-xs font-semibold"><AlertTriangle className="h-3 w-3" /> Over-invested</span>
                            ) : (
                              <span className="inline-flex items-center gap-1 text-emerald-400 text-xs font-semibold"><CheckCircle2 className="h-3 w-3" /> Recovered</span>
                            )}
                          </TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* ========== CREDITS TAB ========== */}
        <TabsContent value="credits">
          <CreditsPanel />
        </TabsContent>
      </Tabs>

      {/* ===== EDIT DIALOGS ===== */}

      {/* Edit Salesman */}
      <Dialog open={showEditSalesman} onOpenChange={setShowEditSalesman}>
        <DialogContent className="bg-slate-800 border-slate-700 text-white">
          <DialogHeader><DialogTitle>Edit Salesman</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div><Label>Full Name</Label><Input value={selectedSalesman?.full_name || ''} onChange={(e) => setSelectedSalesman({...selectedSalesman, full_name: e.target.value})} className="bg-slate-700 border-slate-600" /></div>
            <div><Label>Email</Label><Input type="email" value={selectedSalesman?.email || ''} onChange={(e) => setSelectedSalesman({...selectedSalesman, email: e.target.value})} className="bg-slate-700 border-slate-600" /></div>
            <div className="grid grid-cols-2 gap-4">
              <div><Label>Commission Rate (%)</Label><Input type="number" value={selectedSalesman?.commission_rate || 10} onChange={(e) => setSelectedSalesman({...selectedSalesman, commission_rate: parseFloat(e.target.value)})} className="bg-slate-700 border-slate-600" /></div>
              <div><Label>Basic Salary (R)</Label><Input type="number" value={selectedSalesman?.basic_salary || 2000} onChange={(e) => setSelectedSalesman({...selectedSalesman, basic_salary: parseFloat(e.target.value)})} className="bg-slate-700 border-slate-600" /></div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div><Label>Overtime (R)</Label><Input type="number" min="0" step="0.01" value={selectedSalesman?.overtime ?? 0} onChange={(e) => setSelectedSalesman({...selectedSalesman, overtime: parseFloat(e.target.value) || 0})} className="bg-slate-700 border-slate-600" data-testid="edit-salesman-overtime" /></div>
              <div><Label>Allowance (R)</Label><Input type="number" min="0" step="0.01" value={selectedSalesman?.allowance ?? 0} onChange={(e) => setSelectedSalesman({...selectedSalesman, allowance: parseFloat(e.target.value) || 0})} className="bg-slate-700 border-slate-600" data-testid="edit-salesman-allowance" /></div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div><Label>Incentive (R)</Label><Input type="number" min="0" step="0.01" value={selectedSalesman?.incentive ?? 0} onChange={(e) => setSelectedSalesman({...selectedSalesman, incentive: parseFloat(e.target.value) || 0})} className="bg-slate-700 border-slate-600" data-testid="edit-salesman-incentive" /></div>
              <div><Label>Incentive Note</Label><Input value={selectedSalesman?.incentive_note || ''} onChange={(e) => setSelectedSalesman({...selectedSalesman, incentive_note: e.target.value})} placeholder="e.g. Top seller April" className="bg-slate-700 border-slate-600" data-testid="edit-salesman-incentive-note" /></div>
            </div>
            <div className="bg-amber-950/30 border border-amber-700/40 rounded p-3 space-y-3">
              <p className="text-xs text-amber-300 font-semibold uppercase tracking-wide">Staff Loan Account</p>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Outstanding Loan (R)</Label>
                  <Input type="number" min="0" step="0.01" value={selectedSalesman?.loan_balance ?? 0} onChange={(e) => setSelectedSalesman({...selectedSalesman, loan_balance: parseFloat(e.target.value) || 0})} className="bg-slate-700 border-slate-600" data-testid="edit-salesman-loan-balance" />
                  <p className="text-[11px] text-slate-400 mt-1">Total still owed by this salesman</p>
                </div>
                <div>
                  <Label>This Month's Deduction (R)</Label>
                  <Input type="number" min="0" step="0.01" value={selectedSalesman?.loan_deduction ?? 0} onChange={(e) => setSelectedSalesman({...selectedSalesman, loan_deduction: parseFloat(e.target.value) || 0})} className="bg-slate-700 border-slate-600" data-testid="edit-salesman-loan-deduction" />
                  <p className="text-[11px] text-slate-400 mt-1">Auto-capped at outstanding balance</p>
                </div>
              </div>
            </div>
            <div className="bg-emerald-950/30 border border-emerald-700/40 rounded p-3">
              <p className="text-xs text-emerald-300 font-semibold uppercase tracking-wide mb-2">Target Bonus</p>
              <div>
                <Label>Bonus % when monthly target hit</Label>
                <Input type="number" min="0" max="100" step="0.5" value={selectedSalesman?.target_bonus_rate ?? 10} onChange={(e) => setSelectedSalesman({...selectedSalesman, target_bonus_rate: parseFloat(e.target.value) || 0})} className="bg-slate-700 border-slate-600" data-testid="edit-salesman-target-bonus" />
                <p className="text-[11px] text-slate-400 mt-1">Auto-applied: {selectedSalesman?.target_bonus_rate ?? 10}% of R {(selectedSalesman?.monthly_target || 0).toLocaleString()} = R {(((selectedSalesman?.target_bonus_rate ?? 10) / 100) * (selectedSalesman?.monthly_target || 0)).toLocaleString()} once MTD sales ≥ target</p>
              </div>
            </div>
            <div className="bg-cyan-950/30 border border-cyan-700/40 rounded p-3">
              <p className="text-xs text-cyan-300 font-semibold uppercase tracking-wide mb-2">Fuel Allowance</p>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Monthly Allowance (R)</Label>
                  <Input type="number" min="0" step="0.01" value={selectedSalesman?.fuel_allowance ?? 0} onChange={(e) => setSelectedSalesman({...selectedSalesman, fuel_allowance: parseFloat(e.target.value) || 0})} className="bg-slate-700 border-slate-600" data-testid="edit-salesman-fuel-allowance" />
                </div>
                <div>
                  <Label>Vehicle Reg (optional)</Label>
                  <Input value={selectedSalesman?.fuel_vehicle_reg || ''} onChange={(e) => setSelectedSalesman({...selectedSalesman, fuel_vehicle_reg: e.target.value})} placeholder="e.g. CY12345" className="bg-slate-700 border-slate-600" data-testid="edit-salesman-fuel-reg" />
                </div>
              </div>
              <div className="mt-3 flex items-center justify-between bg-slate-800/60 border border-slate-700 rounded px-3 py-2">
                <div>
                  <p className="text-sm text-white font-medium">Fuel used this month?</p>
                  <p className="text-[11px] text-slate-400">
                    {(selectedSalesman?.fuel_used_flag ?? ((selectedSalesman?.fuel_used || 0) > 0))
                      ? `YES — R ${Number(selectedSalesman?.fuel_allowance || 0).toLocaleString('en-ZA')} consumed, R 0.00 payable in salary.`
                      : `NO — full R ${Number(selectedSalesman?.fuel_allowance || 0).toLocaleString('en-ZA')} paid out in salary.`}
                  </p>
                </div>
                <Switch
                  data-testid="edit-salesman-fuel-used-flag"
                  checked={selectedSalesman?.fuel_used_flag ?? ((selectedSalesman?.fuel_used || 0) > 0)}
                  onCheckedChange={(checked) => {
                    const allow = Number(selectedSalesman?.fuel_allowance || 0);
                    setSelectedSalesman({
                      ...selectedSalesman,
                      fuel_used_flag: checked,
                      fuel_used: checked ? allow : 0,
                    });
                  }}
                />
              </div>
            </div>
            <Button onClick={handleUpdateSalesman} className="w-full bg-amber-600 hover:bg-amber-700">Save Changes</Button>
          </div>
        </DialogContent>
      </Dialog>


      {/* Split Commission Dialog */}
      <Dialog open={showSplitDialog} onOpenChange={(o) => { if (!o) { setShowSplitDialog(false); setSplitSale(null); setSplitDialogRows([]); } }}>
        <DialogContent className="bg-slate-800 border-slate-700 text-white max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Users className="h-5 w-5 text-fuchsia-400" /> Split Commission</DialogTitle>
          </DialogHeader>
          {splitSale && (
            <div className="space-y-3">
              <div className="bg-slate-900/60 border border-slate-700 rounded p-3 text-xs space-y-1">
                <p><span className="text-slate-400">Sale:</span> <span className="text-white">{splitSale.part_description}</span></p>
                <p><span className="text-slate-400">Total:</span> <span className="text-emerald-400 font-bold">{formatCurrency(splitSale.total)}</span></p>
                <p><span className="text-slate-400">Primary:</span> <span className="text-amber-400">{splitSale.salesman_name}</span></p>
              </div>
              <p className="text-xs text-slate-400">
                Add up to 3 salesmen total. Percentages must sum to 100. Leave just the primary at 100% to clear the split.
              </p>
              <div className="space-y-2" data-testid="split-rows">
                {splitDialogRows.map((row, idx) => {
                  const otherIds = splitDialogRows.filter((_, i) => i !== idx).map(r => r.salesman_id).filter(Boolean);
                  const opts = salesmen.filter(sm => !otherIds.includes(sm.id));
                  return (
                    <div key={idx} className="flex items-center gap-2" data-testid={`split-row-${idx}`}>
                      <span className="text-xs text-fuchsia-300 font-bold w-12">#{idx + 1}</span>
                      <Select value={row.salesman_id || ''} onValueChange={(v) => updateSplitRow(idx, { salesman_id: v })}>
                        <SelectTrigger className="bg-slate-700 border-slate-600 flex-1" data-testid={`split-row-salesman-${idx}`}>
                          <SelectValue placeholder="Choose…" />
                        </SelectTrigger>
                        <SelectContent className="bg-slate-700 border-slate-600">
                          {opts.map(sm => <SelectItem key={sm.id} value={sm.id}>{sm.full_name}</SelectItem>)}
                        </SelectContent>
                      </Select>
                      <Input
                        type="number"
                        min="0"
                        max="100"
                        step="0.01"
                        value={row.percent}
                        onChange={(e) => updateSplitRow(idx, { percent: parseFloat(e.target.value) || 0 })}
                        className="bg-slate-700 border-slate-600 w-24"
                        data-testid={`split-row-percent-${idx}`}
                      />
                      <span className="text-sm text-slate-400">%</span>
                      {splitDialogRows.length > 1 && (
                        <button
                          type="button"
                          onClick={() => removeSplitRow(idx)}
                          className="text-rose-400 hover:text-rose-300 text-xs"
                          data-testid={`split-row-remove-${idx}`}
                        >Remove</button>
                      )}
                    </div>
                  );
                })}
              </div>
              {splitDialogRows.length < 3 && (
                <Button
                  type="button"
                  onClick={addSplitRow}
                  className="w-full bg-slate-700 hover:bg-slate-600 text-xs"
                  data-testid="split-add-row-btn"
                >+ Add salesman (auto re-balance)</Button>
              )}
              <div className="bg-slate-900/60 border border-slate-700 rounded p-3 flex items-center justify-between text-sm">
                <span className="text-slate-300">Sum of %</span>
                <span className={`font-bold ${Math.abs(splitDialogRows.reduce((s, r) => s + (parseFloat(r.percent) || 0), 0) - 100) < 0.5 ? 'text-emerald-400' : 'text-rose-400'}`} data-testid="split-rows-sum">
                  {splitDialogRows.reduce((s, r) => s + (parseFloat(r.percent) || 0), 0).toFixed(2)}%
                </span>
              </div>
              <Button onClick={saveSplit} className="w-full bg-fuchsia-600 hover:bg-fuchsia-700" data-testid="split-save-btn">Save Split</Button>
            </div>
          )}
        </DialogContent>
      </Dialog>


      {/* Edit Sale */}
      <Dialog open={showEditSale} onOpenChange={setShowEditSale}>
        <DialogContent className="bg-slate-800 border-slate-700 text-white">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><Pencil className="h-5 w-5" /> Edit Sale</DialogTitle></DialogHeader>
          {editingSale && (
            <div className="space-y-4">
              <div className="bg-slate-700/50 p-3 rounded-lg"><p className="text-sm text-slate-400">Salesman</p><p className="text-white font-medium">{editingSale.salesman_name}</p></div>
              <div><Label>Customer Name</Label><Input value={editingSale.customer_name || ''} onChange={(e) => setEditingSale({...editingSale, customer_name: e.target.value})} placeholder="e.g., Walk-in" className="bg-slate-700 border-slate-600" data-testid="edit-sale-customer-name" /></div>
              <div><Label>Part Description</Label><Input value={editingSale.part_description} onChange={(e) => setEditingSale({...editingSale, part_description: e.target.value})} className="bg-slate-700 border-slate-600" /></div>
              <div className="grid grid-cols-2 gap-4">
                <div><Label>Quantity</Label><Input type="number" min="1" value={editingSale.quantity} onChange={(e) => setEditingSale({...editingSale, quantity: e.target.value})} className="bg-slate-700 border-slate-600" /></div>
                <div><Label>Unit Price (R)</Label><Input type="number" step="0.01" value={editingSale.unit_price} onChange={(e) => setEditingSale({...editingSale, unit_price: e.target.value})} className="bg-slate-700 border-slate-600" /></div>
              </div>
              <div className="bg-slate-700 p-3 rounded-lg"><p className="text-sm text-slate-400">NEW TOTAL:</p><p className="text-2xl font-bold text-emerald-400">{formatCurrency(editSaleTotal)}</p></div>
              <Button onClick={handleEditSale} className="w-full bg-blue-600 hover:bg-blue-700">Update Sale</Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Edit Expense */}
      <Dialog open={showEditExpense} onOpenChange={setShowEditExpense}>
        <DialogContent className="bg-slate-800 border-slate-700 text-white">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><Pencil className="h-5 w-5" /> Edit Expense</DialogTitle></DialogHeader>
          {editingExpense && (
            <div className="space-y-4">
              <div><Label>Description</Label><Input value={editingExpense.description} onChange={(e) => setEditingExpense({...editingExpense, description: e.target.value})} className="bg-slate-700 border-slate-600" /></div>
              <div className="grid grid-cols-2 gap-4">
                <div><Label>Amount (R)</Label><Input type="number" step="0.01" value={editingExpense.amount} onChange={(e) => setEditingExpense({...editingExpense, amount: e.target.value})} className="bg-slate-700 border-slate-600" /></div>
                <div><Label>Date</Label><Input type="date" value={editingExpense.date} onChange={(e) => setEditingExpense({...editingExpense, date: e.target.value})} className="bg-slate-700 border-slate-600" /></div>
              </div>
              <div>
                <Label>Category</Label>
                <Select value={editingExpense.category} onValueChange={(val) => setEditingExpense({...editingExpense, category: val})}>
                  <SelectTrigger className="bg-slate-700 border-slate-600"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-slate-700 border-slate-600">{EXPENSE_CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <Button onClick={handleEditExpense} className="w-full bg-blue-600 hover:bg-blue-700">Update Expense</Button>
            </div>
          )}
        </DialogContent>
      </Dialog>


      {/* Stock Alerts Dialog — opens from the Low-stock card */}
      <Dialog open={showStockAlerts} onOpenChange={setShowStockAlerts}>
        <DialogContent className="bg-slate-800 border-rose-700 text-white max-w-3xl max-h-[85vh]" data-testid="stock-alerts-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-rose-300">
              <AlertTriangle className="h-5 w-5" /> Low Stock Alerts
              {stockAlerts?.counts && (
                <span className="text-xs text-slate-400 font-normal ml-2">
                  as of {new Date(stockAlerts.generated_at).toLocaleTimeString()}
                </span>
              )}
            </DialogTitle>
          </DialogHeader>
          {stockAlerts && (
            <div className="space-y-3 overflow-y-auto max-h-[65vh]">
              <div className="grid grid-cols-4 gap-2 text-xs">
                <div className="bg-rose-950/40 border border-rose-700 rounded p-2 text-center">
                  <div className="text-rose-300 text-[10px] uppercase">Out of Stock</div>
                  <div className="text-xl text-white font-bold">{stockAlerts.counts.out_of_stock}</div>
                </div>
                <div className="bg-amber-950/40 border border-amber-700 rounded p-2 text-center">
                  <div className="text-amber-300 text-[10px] uppercase">Below Target</div>
                  <div className="text-xl text-white font-bold">{stockAlerts.counts.below_target}</div>
                </div>
                <div className="bg-slate-900 border border-slate-700 rounded p-2 text-center">
                  <div className="text-rose-300 text-[10px] uppercase">Fast OOS</div>
                  <div className="text-xl text-rose-400 font-bold">{stockAlerts.counts.fast_out_of_stock}</div>
                </div>
                <div className="bg-slate-900 border border-slate-700 rounded p-2 text-center">
                  <div className="text-amber-300 text-[10px] uppercase">Fast Low</div>
                  <div className="text-xl text-amber-400 font-bold">{stockAlerts.counts.fast_below_target}</div>
                </div>
              </div>
              {stockAlerts.out_of_stock.length > 0 && (
                <div>
                  <h4 className="text-rose-300 font-semibold text-sm mb-1 flex items-center gap-1">
                    <AlertTriangle className="h-4 w-4" /> Out of Stock ({stockAlerts.out_of_stock.length})
                  </h4>
                  <div className="rounded border border-slate-700">
                    <Table>
                      <TableHeader className="bg-slate-900/80">
                        <TableRow className="border-slate-700 hover:bg-transparent">
                          <TableHead className="text-slate-400 text-xs">Code</TableHead>
                          <TableHead className="text-slate-400 text-xs">Item</TableHead>
                          <TableHead className="text-slate-400 text-xs">Category</TableHead>
                          <TableHead className="text-slate-400 text-xs text-center">Priority</TableHead>
                          <TableHead className="text-slate-400 text-xs text-right">Target</TableHead>
                          <TableHead className="text-slate-400 text-xs text-right">Short By</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {stockAlerts.out_of_stock.map((r) => (
                          <TableRow key={r.id} className="border-slate-800 hover:bg-slate-800/50"
                            data-testid={`alert-oos-${r.supplier_sku || r.id}`}>
                            <TableCell className="font-mono text-amber-300 text-xs">{r.supplier_sku || '—'}</TableCell>
                            <TableCell className="text-white text-sm">{r.name}</TableCell>
                            <TableCell className="text-slate-400 text-xs">{r.category || '—'}</TableCell>
                            <TableCell className="text-center">
                              {r.priority === 'Fast' && <Badge className="bg-rose-600 text-white text-[10px]">Fast</Badge>}
                              {r.priority === 'Medium' && <Badge className="bg-amber-500 text-white text-[10px]">Medium</Badge>}
                              {r.priority === 'Slow' && <Badge className="bg-slate-500 text-white text-[10px]">Slow</Badge>}
                              {r.priority === 'Unset' && <span className="text-slate-500 text-[10px]">—</span>}
                            </TableCell>
                            <TableCell className="text-right font-mono text-slate-400 text-xs">{r.target_stock_level || '—'}</TableCell>
                            <TableCell className="text-right font-mono text-rose-400 text-xs font-semibold">{r.shortfall || '—'}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                </div>
              )}
              {stockAlerts.below_target.length > 0 && (
                <div>
                  <h4 className="text-amber-300 font-semibold text-sm mb-1 flex items-center gap-1">
                    <AlertTriangle className="h-4 w-4" /> Below Target ({stockAlerts.below_target.length})
                  </h4>
                  <div className="rounded border border-slate-700">
                    <Table>
                      <TableHeader className="bg-slate-900/80">
                        <TableRow className="border-slate-700 hover:bg-transparent">
                          <TableHead className="text-slate-400 text-xs">Code</TableHead>
                          <TableHead className="text-slate-400 text-xs">Item</TableHead>
                          <TableHead className="text-slate-400 text-xs">Category</TableHead>
                          <TableHead className="text-slate-400 text-xs text-center">Priority</TableHead>
                          <TableHead className="text-slate-400 text-xs text-right">On Hand</TableHead>
                          <TableHead className="text-slate-400 text-xs text-right">Target</TableHead>
                          <TableHead className="text-slate-400 text-xs text-right">Short By</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {stockAlerts.below_target.map((r) => (
                          <TableRow key={r.id} className="border-slate-800 hover:bg-slate-800/50"
                            data-testid={`alert-low-${r.supplier_sku || r.id}`}>
                            <TableCell className="font-mono text-amber-300 text-xs">{r.supplier_sku || '—'}</TableCell>
                            <TableCell className="text-white text-sm">{r.name}</TableCell>
                            <TableCell className="text-slate-400 text-xs">{r.category || '—'}</TableCell>
                            <TableCell className="text-center">
                              {r.priority === 'Fast' && <Badge className="bg-rose-600 text-white text-[10px]">Fast</Badge>}
                              {r.priority === 'Medium' && <Badge className="bg-amber-500 text-white text-[10px]">Medium</Badge>}
                              {r.priority === 'Slow' && <Badge className="bg-slate-500 text-white text-[10px]">Slow</Badge>}
                              {r.priority === 'Unset' && <span className="text-slate-500 text-[10px]">—</span>}
                            </TableCell>
                            <TableCell className="text-right font-mono text-amber-300 text-xs">{r.quantity}</TableCell>
                            <TableCell className="text-right font-mono text-slate-400 text-xs">{r.target_stock_level}</TableCell>
                            <TableCell className="text-right font-mono text-amber-400 text-xs font-semibold">{r.shortfall}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                </div>
              )}
              {stockAlerts.counts.total_alerts === 0 && (
                <div className="text-center py-10 text-emerald-400">
                  <CheckCircle2 className="h-8 w-8 mx-auto mb-2" />
                  <p className="font-semibold">Nothing to reorder — every priced product is at or above target.</p>
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button onClick={() => setShowStockAlerts(false)} className="bg-slate-700 hover:bg-slate-600 text-white" data-testid="stock-alerts-close-btn">Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Product */}
      <Dialog open={showEditProduct} onOpenChange={setShowEditProduct}>  <DialogContent className="bg-slate-800 border-slate-700 text-white max-w-lg">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><Pencil className="h-5 w-5" /> Edit Product</DialogTitle></DialogHeader>
          {editingProduct && (
            <div className="space-y-4">
              <div><Label>Name</Label><Input value={editingProduct.name} onChange={(e) => setEditingProduct({...editingProduct, name: e.target.value})} className="bg-slate-700 border-slate-600" /></div>
              <div>
                <Label>Category</Label>
                <Select value={editingProduct.category} onValueChange={(val) => setEditingProduct({...editingProduct, category: val})}>
                  <SelectTrigger className="bg-slate-700 border-slate-600"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-slate-700 border-slate-600 max-h-60">{categories.map((c) => <SelectItem key={c.name} value={c.name}>{c.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div><Label>Cost Price (R)</Label><Input type="number" step="0.01" value={editingProduct.cost_price} onChange={(e) => setEditingProduct({...editingProduct, cost_price: e.target.value})} className="bg-slate-700 border-slate-600" /></div>
                <div><Label>Sell Price (R)</Label><Input type="number" step="0.01" value={editingProduct.selling_price} onChange={(e) => setEditingProduct({...editingProduct, selling_price: e.target.value})} className="bg-slate-700 border-slate-600" /></div>
                <div><Label>Quantity</Label><Input type="number" min="0" value={editingProduct.quantity} onChange={(e) => setEditingProduct({...editingProduct, quantity: e.target.value})} className="bg-slate-700 border-slate-600" /></div>
              </div>
              <div><Label>Vehicle Source</Label><Input value={editingProduct.vehicle_source || ''} onChange={(e) => setEditingProduct({...editingProduct, vehicle_source: e.target.value})} className="bg-slate-700 border-slate-600" /></div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label>Target Stock Level</Label>
                  <Input
                    type="number" min="0"
                    value={editingProduct.target_stock_level ?? ''}
                    onChange={(e) => setEditingProduct({...editingProduct, target_stock_level: e.target.value === '' ? '' : parseInt(e.target.value, 10)})}
                    className="bg-slate-700 border-slate-600"
                    data-testid="edit-target-stock-level"
                    placeholder="e.g. 10"
                  />
                </div>
                <div>
                  <Label>Priority</Label>
                  <Select
                    value={editingProduct.priority || '__none__'}
                    onValueChange={(val) => setEditingProduct({...editingProduct, priority: val === '__none__' ? '' : val})}
                  >
                    <SelectTrigger className="bg-slate-700 border-slate-600" data-testid="edit-priority">
                      <SelectValue placeholder="Select priority" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__none__">—</SelectItem>
                      <SelectItem value="Fast">Fast</SelectItem>
                      <SelectItem value="Medium">Medium</SelectItem>
                      <SelectItem value="Slow">Slow</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <Button onClick={handleEditProduct} className="w-full bg-blue-600 hover:bg-blue-700" data-testid="edit-product-save-btn">Update Product</Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
